'use client';
/**
 * A3 — LA VUE MENSUELLE DU PLANNING.
 *
 * ── Ce qu'elle sert, et ce qu'elle ne sert pas ─────────────────────────────
 *
 * La grille de la SEMAINE est un outil d'écriture : on y affecte, on y déroge,
 * chaque case porte un sélecteur. Étendue à trente et un jours, elle
 * deviendrait illisible — et surtout dangereuse : un mois de cases cliquables
 * invite à planifier au kilomètre, loin des habilitations qu'on vérifie case
 * par case.
 *
 * Le mois est donc un outil de LECTURE, et rien d'autre. Aucune case ne
 * s'écrit ici. Il répond à trois questions que la semaine ne sait pas poser :
 *   · où sont les trous de couverture sur la durée ?
 *   · qui porte la charge, et la rotation tourne-t-elle vraiment ?
 *   · le mois tient-il sur une feuille qu'on affiche en salle ?
 *
 * ⚠️ AUCUN CALCUL D'OUVERTURE ICI. L'état d'un poste un jour donné (ouvert,
 * fermé, indéterminé) vient du serveur, sur la même plage — la règle de
 * préséance (exception > férié > règle du jour) est délicate et testée, et une
 * seconde implémentation côté navigateur finirait par en diverger.
 *
 * Les initiales sont COLORÉES par une teinte stable, dérivée du code de la
 * personne ou du poste. Un mois entier ne se lit pas au texte : c'est la
 * couleur qui fait voir d'un coup d'œil qu'une rotation s'est arrêtée.
 */
import React from 'react';
import { createPortal } from 'react-dom';
import { enTeteCollant } from '@/shared/Tableau';
import { useGed } from '@/lib/store';
import { peutLirePlanning, peutVoirCommentairePlanning, peutEcrireCommentairePlanning } from '@/lib/acces-planning';
import { useOccurrences, parDate, pistesConventions } from '@/lib/conventions';
/* `BandeauConventions` n'est plus importé : il n'était pas rendu dans cet
   écran (la liste des conventions de la période vit au-dessus de la grille
   « En cours »). La LÉGENDE, elle, est propre au mensuel — voir plus bas. */
import { LignesConventions, LegendeConventions } from '@/components/BandeauConventions';
import { grouperLignes, blocDeCategorie, CATEGORIES_POSTE, BLOC_AUTRES } from '@/lib/blocs-planning';
import { abregesPostes, abregePoste } from '@/lib/abrege-poste';
import type { RhAbsence, RhAffectation, RhOuverture, RhPersonnel, RhPoste } from '@/lib/types';
import { I, card, EmptyState, Skeleton, inputStyle } from '@/components/ui';
import { fetchEcriture } from '@/lib/ecrire';
import { echangePossible, refusRemplacement, verdictLocal } from '@/lib/echange-planning';
import { ChoixRemplacant, type CandidatRemplacement } from '@/components/ChoixRemplacant';
import { EchangeContext, PastilleEchangeable, type PastilleEchange } from '@/components/EchangeDnd';

const JOURS_COURTS = ['D', 'L', 'M', 'M', 'J', 'V', 'S'];
const MOIS_COURTS_CHOIX = ['janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin', 'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.'];
const MOIS_NOMS = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];

/** Les jours du mois `AAAA-MM`, en ISO. */
export function joursDuMois(mois: string): string[] {
  const [a, m] = mois.split('-').map(Number);
  const n = new Date(a, m, 0).getDate();
  return Array.from({ length: n }, (_, i) => `${mois}-${String(i + 1).padStart(2, '0')}`);
}

/** Décale un mois `AAAA-MM` de `n` mois. */
export function decalerMois(mois: string, n: number): string {
  const [a, m] = mois.split('-').map(Number);
  const d = new Date(a, m - 1 + n, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

/* ⚠️ L'ALGORITHME A DÉMÉNAGÉ dans `@/lib/couleurs-personne` (Lot 2,
   02/09/2026) — inchangé au caractère près. Il vivait ici, dans l'écran du
   mois, alors qu'il décide de l'apparence d'une personne sur TOUS les écrans :
   le mois, la semaine, l'organigramme. Une couleur choisie par la RQ devait
   pouvoir s'y substituer partout à la fois ; deux implémentations auraient fait
   diverger l'apparence d'un écran à l'autre, ce que la spec interdit
   explicitement.
   Réexporté ici : les deux noms sont cités dans le reste du fichier, et un
   renommage de plus n'apprendrait rien à personne. */
export { teinte, pastilleDe } from '@/lib/couleurs-personne';
/* LE MODE JOUR (RQ, 05/10/2026) — « quand je clique sur une journée, je veux la
   voir en mode jour ». Ici la journée est celle du LABORATOIRE : quels postes
   sont ouverts, qui les tient, et ce que personne ne tient. */
import { FenetreJour, RubriqueJour } from '@/components/FenetreJour';
import { libelleJourLong } from '@/lib/jour';
import { pointsAttention, resumeAttention } from '@/lib/points-attention-planning';
/* ⚠️ RIEN N'EST IMPORTÉ DU MODULE DES JOURS FÉRIÉS, et c'est un INVARIANT de
   cet écran (`qualite-planning-mois` le vérifie, et il a rattrapé une première
   version de ce code) : la vue mensuelle ne porte PAS la notion de férié. Elle
   lit les jours CHÔMÉS que le moteur lui sert (`joursChomesDuMois`) et ne sait
   pas pourquoi un jour l'est — une seconde source de vérité sur les fériés
   finirait par contredire la première.
   La fenêtre du jour dit donc « jour chômé », sans nommer la fête : le nom se
   lit dans « Mon planning » et dans le calendrier du laboratoire, qui ont la
   liste pour de bon. */
import { pastilleDe, initialesSansCollision } from '@/lib/couleurs-personne';
import { nomDe } from '@/lib/nom';
import { FAMILLES_PERSONNEL, familleDe, sansTitre } from '@/lib/familles-personnel';

import { Bouton } from '@/shared/Bouton';
import { Segment } from '@/shared/Segment';
import { isoJour, fmtDateHeure } from '@/lib/meta';
/**
 * L'IMPRESSION A4 PAYSAGE, EN COULEUR (RQ, 30/09/2026 : « au format A4 le
 * planning est illisible en initiales »).
 *
 * L'ancienne règle réduisait toute la grille à 52 % (`zoom: 0.52`) : une
 * pastille de 9 px sortait à ~3,5 pt sur le papier. On imprime désormais à la
 * taille réelle, avec des tailles en POINTS pensées pour le papier : largeur
 * fixe de la colonne de tête, trente et une colonnes égales pour le reste (≈ 8 mm
 * chacune, de quoi porter trois lettres en 7,5 pt gras), lignes resserrées pour
 * tenir sur une page, et couleurs forcées (sans `print-color-adjust`, le
 * navigateur supprime les fonds des pastilles).
 *
 * `p` préfixe les sélecteurs : '' pour `@media print`.
 */
const IMPRESSION = (p: string) => `
  @page { size: A4 landscape; margin: 5mm; }
  body * { visibility: hidden; }
  ${p}.planning-mois, ${p}.planning-mois * { visibility: visible; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  ${p}.planning-mois { position: absolute; left: 0; top: 0; width: 100%; }
  ${p}.planning-mois button:not(.pm-jour), ${p}.planning-mois select, ${p}.planning-mois .pm-ecran { display: none !important; }
  ${p}.planning-mois button.pm-jour { text-decoration: none !important; }
  ${p}.planning-mois button.pm-jour * { text-decoration: none !important; }
  ${p}.planning-mois .mois-defile { overflow: visible !important; box-shadow: none !important; border: none !important; }
  ${p}.planning-mois table { min-width: 0 !important; width: 100% !important; table-layout: fixed !important; }
  ${p}.planning-mois col.pm-col-tete { width: 40mm !important; }
  ${p}.planning-mois col.pm-col-jour-tete { width: 11mm !important; }
  ${p}.planning-mois col.pm-col-total { width: 5.5mm !important; }
  ${p}.planning-mois th, ${p}.planning-mois td { position: static !important; height: auto !important; padding: 0.3mm !important; border-color: #B8B8B4 !important; }
  ${p}.planning-mois thead th { font-size: 7pt !important; }
  ${p}.planning-mois tbody th { font-size: 7.5pt !important; }
  ${p}.planning-mois .pm-pastille { font-size: 7.5pt !important; line-height: 1.05 !important; padding: 0 0.5mm !important; letter-spacing: 0 !important; }
  ${p}.planning-mois .pm-pastille.pm-absente { opacity: 0.6 !important; }
  ${p}.planning-mois .pm-attenue { opacity: 1 !important; }
  ${p}.planning-mois .pm-pastille.pm-absente.pm-attenue { opacity: 0.6 !important; }
  ${p}.planning-mois .pm-abs { font-size: 6.5pt !important; }
  ${p}.planning-mois tr { break-inside: avoid; }
  ${p}.planning-mois .pm-barre { margin: 0 0 1mm !important; }
  ${p}.planning-mois .pm-commentaire-saisie { display: none !important; }
  ${p}.planning-mois .pm-commentaire-imprime { display: block !important; font-size: 8pt !important; white-space: pre-wrap; flex: 1; }
  ${p}.planning-mois .pm-barre strong { font-size: 10pt !important; text-align: start !important; }
  ${p}.planning-mois .pm-barre .pm-titre-feuille { display: inline-block !important; font-size: 14pt !important; font-weight: 800 !important; text-transform: capitalize; }
  ${p}.planning-mois .pm-titre, ${p}.planning-mois .pm-sous { white-space: nowrap !important; overflow: hidden !important; text-overflow: ellipsis !important; line-height: 1.05 !important; }
  ${p}.planning-mois .pm-sous { font-size: 5.5pt !important; }
  ${p}.planning-mois tbody th[colspan] { font-size: 6.5pt !important; padding: 0.4mm 1mm !important; }
  ${p}.planning-mois .pm-legende { font-size: 7pt !important; margin-top: 1.5mm !important; gap: 1mm 2.5mm !important; }
  ${p}.planning-mois .pm-legende strong { font-size: 7pt !important; }
  ${p}.planning-mois .pm-legende .pm-pastille { font-size: 6.5pt !important; }
`;

/**
 * LE PDF GÉNÉRÉ PAR L'ÉCRAN (RQ, 30/09/2026 : « le PDF n'est pas à l'endroit,
 * il faut trois quarts de tour »). Selon la fenêtre d'impression, macOS
 * remet la page en portrait et couche le planning dessus : on ne maîtrise pas
 * la fenêtre du système. Le bouton « PDF » photographie donc la grille avec les
 * MÊMES règles que l'impression (dérivées d'`IMPRESSION`, sans la page ni le
 * masquage du reste de l'écran) et écrit lui-même un A4 PAYSAGE — toujours à
 * l'endroit.
 */
const CAPTURE = IMPRESSION('')
  .split('\n')
  .filter((l) => !/@page|body \*|visibility|position: absolute/.test(l))
  .join('\n')
  .replace(/\.planning-mois/g, '.planning-mois.pm-capture');

/**
 * LE NOMBRE DE VACATIONS D'UN JOUR (RQ, 30/09/2026) : « deux postes sur deux
 * plages différentes se comptent en 2 vacations, même si c'est un seul
 * dimanche ». Deux postes sur la MÊME plage (Accueil +++ et PLV +++ de 13 h à
 * 19 h) restent une seule vacation. La garde de nuit se compte à part.
 */
function vacationsDuJour(cases: { heureDebut?: string | null; heureFin?: string | null; poste?: { code?: string } | null }[]): number {
  return new Set(cases.filter((c) => c.poste?.code !== 'GARDE')
    .map((c) => `${c.heureDebut || ''}-${c.heureFin || ''}`)).size;
}

const cellBase: React.CSSProperties = {
  border: '1px solid var(--border-subtle)', padding: 0, height: 26,
  /* EN HAUT, pas au milieu (RQ, 29/09/2026) : un jour de garde porte deux
     pastilles, et centrées elles décalaient le poste du jour par rapport aux
     cases voisines — la ligne ne se lisait plus à l'horizontale. */
  textAlign: 'center', verticalAlign: 'top', fontSize: 12.5,
};

/** Clé de stockage du filtre de catégories — propre à ce navigateur. */
const CLE_FILTRE = 'bio24.planningMois.categories';

/**
 * LE TITRE DU MOIS, CLIQUABLE (RQ, 30/09/2026 : « en cliquant sur le mois, je
 * veux choisir un mois et une année directement »). Un petit panneau : l'année
 * avec ses flèches, et les douze mois. Pas de `<select>` : la vue mensuelle
 * n'en porte aucun (invariant `qualite-planning-mois`). Le titre reste un
 * `<strong>` pour sortir à l'impression.
 */
export function ChoixMois({ mois, onChoisir }: { mois: string; onChoisir: (m: string) => void }) {
  const [a, m] = mois.split('-').map(Number);
  const [ouvert, setOuvert] = React.useState(false);
  const [annee, setAnnee] = React.useState(a);
  const ref = React.useRef<HTMLSpanElement>(null);
  React.useEffect(() => {
    if (!ouvert) return;
    const fermer = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOuvert(false); };
    const echap = (e: KeyboardEvent) => { if (e.key === 'Escape') setOuvert(false); };
    document.addEventListener('mousedown', fermer); document.addEventListener('keydown', echap);
    return () => { document.removeEventListener('mousedown', fermer); document.removeEventListener('keydown', echap); };
  }, [ouvert]);
  const basculer = () => { setAnnee(a); setOuvert(!ouvert); };
  return (
    <span ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
      <strong role="button" tabIndex={0} aria-haspopup="dialog" aria-expanded={ouvert} title="Choisir un mois et une année"
        onClick={basculer} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); basculer(); } }}
        style={{ fontSize: 14, minWidth: 148, textAlign: 'center', display: 'inline-block', cursor: 'pointer',
          padding: '4px 8px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-subtle)' }}>
        {MOIS_NOMS[m - 1]} {a} <span className="pm-ecran" aria-hidden style={{ fontSize: 10, color: 'var(--text-muted)' }}>▾</span>
      </strong>
      {ouvert && (
        <div role="dialog" aria-label="Choisir un mois" className="pm-ecran"
          style={{ position: 'absolute', top: '110%', left: 0, zIndex: 20, background: 'var(--surface-raised)',
            border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', boxShadow: '0 6px 24px rgba(0,0,0,.14)', padding: 10, width: 240 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <Bouton taille="sm" type="button" aria-label="Année précédente" onClick={() => setAnnee(annee - 1)}><I n="chevron-left" s={13} /></Bouton>
            <strong style={{ fontSize: 14 }}>{annee}</strong>
            <Bouton taille="sm" type="button" aria-label="Année suivante" onClick={() => setAnnee(annee + 1)}><I n="chevron-right" s={13} /></Bouton>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 4 }}>
            {MOIS_NOMS.map((nom, i) => {
              const cle = `${annee}-${String(i + 1).padStart(2, '0')}`;
              const actif = cle === mois;
              return (
                <Bouton variante={actif ? 'primaire' : 'contour'} taille="sm" key={cle} type="button" aria-pressed={actif} onClick={() => { onChoisir(cle); setOuvert(false); }}>
                  {MOIS_COURTS_CHOIX[i] || nom}
                </Bouton>
              );
            })}
          </div>
        </div>
      )}
    </span>
  );
}

/**
 * LE COMMENTAIRE DU MOIS (RQ, 30/09/2026 : « une case commentaires sur le
 * planning du mois, qui s'édite en face d'Octobre 2026 »). Enregistré en
 * quittant la case ; imprimé à côté du titre. Chaque modification est
 * journalisée côté moteur.
 */
function CommentaireMois({ mois, modifiable }: { mois: string; modifiable: boolean }) {
  /* ── UNE NOTE QU'ON LIT, PUIS QU'ON CHOISIT DE MODIFIER (RQ, 26/10/2026) ─────
   * « L'encart de texte, on ne comprend pas trop ; il est trop facilement
   * modifiable. » C'était une zone de saisie OUVERTE en permanence, sans titre,
   * enregistrée au simple départ du curseur : un clic puis une frappe, et le
   * texte imprimé avec le planning changeait, signature comprise.
   *
   * Désormais :
   *  · LECTURE d'abord — la note s'affiche comme un texte, sous son titre, avec
   *    ce qu'elle est (« imprimée avec le planning ») et qui l'a écrite ;
   *  · MODIFIER est un geste : un bouton ouvre la saisie, et l'enregistrement
   *    est un autre geste (« Enregistrer », ou Ctrl+Entrée). « Annuler » et
   *    Échap rendent la note intacte ;
   *  · sans note, qui n'a pas le droit d'écrire ne voit RIEN (pas de case vide) ;
   *    qui l'a voit une seule invitation, pas un champ.
   * Un texte vidé puis enregistré RETIRE la note — et le bouton le dit. */
  const [enregistre, setEnregistre] = React.useState<string | null>(null);
  const [brouillon, setBrouillon] = React.useState('');
  const [edition, setEdition] = React.useState(false);
  const [etat, setEtat] = React.useState<'' | 'envoi' | 'erreur'>('');
  const [info, setInfo] = React.useState('');
  const lire = useGed((s) => s.loadCommentairePlanning);
  const ecrire = useGed((s) => s.enregistrerCommentairePlanning);
  const signature = (c: { parNom?: string; le?: string } | null) => (c?.le ? `${c.parNom || ''}, le ${fmtDateHeure(c.le, false)}` : '');
  const [a, m] = mois.split('-').map(Number);
  const nomMois = `${MOIS_NOMS[m - 1]} ${a}`;
  React.useEffect(() => {
    let vivant = true;
    void lire(mois).then((c) => {
      if (!vivant) return;
      setEnregistre(String(c?.texte || '')); setInfo(signature(c));
    });
    return () => { vivant = false; };
  }, [mois, lire]);

  const ouvrir = () => { setBrouillon(enregistre || ''); setEtat(''); setEdition(true); };
  const annuler = () => { setEdition(false); setEtat(''); };
  const sauver = async () => {
    if (etat === 'envoi') return;
    if (brouillon.trim() === (enregistre || '').trim()) { setEdition(false); return; }
    setEtat('envoi');
    const r = await ecrire(mois, brouillon);
    if (!r.ok) { setEtat('erreur'); return; } // le brouillon reste : rien n'est perdu
    setEnregistre(brouillon.trim()); setInfo(signature(r.commentaire)); setEtat(''); setEdition(false);
  };

  if (enregistre == null) return null; // chargement : ni note ni invitation affirmées avant de savoir
  const vide = !enregistre.trim();
  if (vide && !modifiable) return null;

  const cadre: React.CSSProperties = {
    flex: '1 1 320px', minWidth: 240, maxWidth: 720, padding: '8px 12px', borderRadius: 'var(--radius-sm)',
    border: '1px solid var(--border-subtle)', background: 'var(--gray-25)',
  };
  return (
    <>
      <div className="pm-commentaire-saisie" style={{ flex: '1 1 320px', minWidth: 240, maxWidth: 720 }}>
        {edition ? (
          <div style={cadre}>
            <label htmlFor="pm-note" style={{ display: 'block', fontSize: 12.5, fontWeight: 700, marginBottom: 2 }}>Note du mois — {nomMois}</label>
            <p style={{ margin: '0 0 6px', fontSize: 12, color: 'var(--text-muted)' }}>
              Cette note est imprimée avec le planning du mois, sous le titre. Rien n’est enregistré avant « Enregistrer ».
            </p>
            <textarea id="pm-note" value={brouillon} rows={3} autoFocus disabled={etat === 'envoi'}
              onChange={(e) => { setBrouillon(e.target.value); setEtat(''); }}
              onKeyDown={(e) => {
                if (e.key === 'Escape') { e.preventDefault(); annuler(); }
                if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); void sauver(); }
              }}
              style={{ ...inputStyle, width: '100%', resize: 'vertical', fontSize: 13, minHeight: 64, padding: '6px 8px' }} />
            {etat === 'erreur' && (
              <p role="alert" style={{ margin: '6px 0 0', fontSize: 12, fontWeight: 700, color: 'var(--red-600)' }}>
                Non enregistrée (serveur injoignable ou droit manquant). Votre texte est resté dans la case : réessayez.
              </p>
            )}
            <div style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'center' }}>
              <Bouton variante="primaire" taille="sm" type="button" disabled={etat === 'envoi'} onClick={() => void sauver()}>
                {etat === 'envoi' ? 'Enregistrement…' : (!brouillon.trim() && !vide ? 'Retirer la note' : 'Enregistrer')}
              </Bouton>
              <Bouton taille="sm" type="button" disabled={etat === 'envoi'} onClick={annuler}>Annuler</Bouton>
              <span style={{ fontSize: 11, color: 'var(--text-faint)' }}>Ctrl+Entrée pour enregistrer · Échap pour annuler</span>
            </div>
          </div>
        ) : vide ? (
          <Bouton variante="discret" taille="sm" type="button" onClick={ouvrir}
            title="Une note libre, imprimée avec le planning du mois (consigne, rappel, fermeture…)">
            <I n="plus" s={13} /> Ajouter une note au planning de {nomMois}
          </Bouton>
        ) : (
          <div style={cadre}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
              <strong style={{ fontSize: 12.5 }}>Note du mois</strong>
              <span style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>imprimée avec le planning</span>
              <span style={{ flex: 1 }} />
              {modifiable && <Bouton variante="discret" taille="sm" type="button" onClick={ouvrir}>Modifier</Bouton>}
            </div>
            <p style={{ margin: '4px 0 0', fontSize: 13, lineHeight: 1.45, whiteSpace: 'pre-wrap' }}>{enregistre}</p>
            {info && <span style={{ display: 'block', marginTop: 4, fontSize: 11, color: 'var(--text-faint)' }}>Dernière modification : {info}</span>}
          </div>
        )}
      </div>
      {enregistre.trim() && <div className="pm-commentaire-imprime" style={{ display: 'none' }}>{enregistre}</div>}
    </>
  );
}

export function PlanningMois({ vue, sens, modeEdition, emplacements }: {
  vue: 'poste' | 'personne'; sens: 'jours-en-colonnes' | 'postes-en-colonnes';
  /** Le mode « Modifier » de la barre commune (ouvert à la main, fermé par défaut). */
  modeEdition: boolean;
  /** Où poser les commandes propres au mois dans la barre de l'écran parent
   *  (celle de la semaine et du jour) : `actions` à droite de la ligne du haut,
   *  `affichage` sur la ligne des réglages. Tant qu'ils ne sont pas montés, rien
   *  ne s'affiche — jamais deux barres. */
  emplacements?: { actions: HTMLElement | null; affichage: HTMLElement | null };
}) {
  const {
    rhPostes, rhPersonnel, rhAbsences, rhChargement, rhAcces, session,
    rhMois, setRhMois, loadRhMois, lireAffectationsDuJour, rhAffectationsMois, rhOuverturesMois, rhMoisCharge, joursChomesDuMois,
    rhPlanningModifiable, rhDetenues, showToast, loadMaintenances,
  } = useGed();
  const sectionRef = React.useRef<HTMLElement>(null);
  const [pdfEnCours, setPdfEnCours] = React.useState(false);
  /* Le jour ouvert en fenêtre, ou `null`. ⚠️ Il reste DANS le mois affiché : la
     fenêtre lit `rhAffectationsMois`, et naviguer hors du mois n'aurait rien
     montré. Les flèches sont donc bornées (voir `jourDansLeMois`). */
  const [jourOuvert, setJourOuvert] = React.useState<string | null>(null);
  const exporterPdf = async () => {
    const el = sectionRef.current;
    if (!el) return;
    setPdfEnCours(true);
    el.classList.add('pm-capture');
    el.style.width = '1085px';
    try {
      await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
      const [{ toPng }, { jsPDF }] = await Promise.all([import('html-to-image'), import('jspdf')]);
      const png = await toPng(el, {
        pixelRatio: 3, backgroundColor: '#ffffff',
        filter: (n) => !(n instanceof HTMLElement && ((n.tagName === 'BUTTON' && !n.classList.contains('pm-jour')) || n.tagName === 'SELECT'
          || n.classList.contains('pm-ecran') || n.classList.contains('pm-commentaire-saisie'))),
      });
      const img = new Image(); img.src = png; await img.decode();
      const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
      const r = Math.min(287 / img.width, 200 / img.height);
      doc.addImage(png, 'PNG', 5, 5, img.width * r, img.height * r);
      doc.save(`planning-${rhMois}.pdf`);
    } finally {
      el.classList.remove('pm-capture');
      el.style.width = '';
      setPdfEnCours(false);
    }
  };

  /* QUI A LE DROIT DE LIRE CE MOIS — la même question que la vue Semaine, et
     la même réponse (12/09/2026). `rhAcces.acces`, c'est le rôle `rh` ; la
     section `planning` porte la règle `lectureLarge`, et le serveur sert le
     mois à quiconque a `planning.voir`. Tester le seul rôle ici revenait à
     refermer côté écran une rubrique que le moteur avait ouverte. */
  const peutLire = peutLirePlanning(rhAcces, session.sections);
  /* ⚠️ `rhPersonnel` N'EST PAS CHARGÉ pour qui n'a que le planning : la route
     `/ged/rh/personnel` exige le rôle `rh` (matricule, RIB, dates de sortie —
     rien qui regarde un planning), et `loadRh` ne la demande donc pas. Sans ce
     repli, la correction ci-dessus aurait rendu un écran à peine moins faux :
     la vue « par personne » sans AUCUNE ligne, et la vue « par poste » sans
     initiales ni couleurs.
     L'effectif se DÉRIVE alors des affectations, qui portent déjà leur
     personne en entier (`populate: { personnel: true }`). Ce n'est pas une
     approximation : le planning montre qui tient quel poste, donc les gens
     qu'il nomme SONT ceux qu'il a à montrer. Aucune donnée nouvelle n'est
     exposée — c'est exactement ce que `lectureLarge` a décidé d'ouvrir.
     ⚠️ Dérivé du MOIS affiché : quelqu'un qui n'a aucune affectation ce
     mois-là n'a pas de ligne. C'est assumé — la RH, elle, voit tout le monde,
     y compris les non planifiés, parce que c'est précisément son sujet. */
  const effectif = React.useMemo(() => {
    if (rhPersonnel.length) return rhPersonnel;
    const par = new Map<string, RhPersonnel>();
    for (const a of rhAffectationsMois) {
      const p = a.personnel;
      if (p?.orgId && !par.has(p.orgId)) par.set(p.orgId, p);
    }
    return [...par.values()];
  }, [rhPersonnel, rhAffectationsMois]);

  React.useEffect(() => { void loadRhMois(); }, [loadRhMois, rhMois]);

  const jours = React.useMemo(() => joursDuMois(rhMois), [rhMois]);
  /* Les conventions du mois : un repère sous la date, et la liste au-dessus de la grille. */
  const { occurrences: occConventions } = useOccurrences(jours[0] || '', jours[jours.length - 1] || '');
  const conventionsDuJour = React.useMemo(() => parDate(occConventions), [occConventions]);
  /* LES PISTES DU MOIS : une ligne réservée par clinique, dans l'ordre du
     moteur, pour que la même couleur reste à la même hauteur tous les jours
     (Inès, 05/10/2026). La légende sous la grille lit la MÊME liste, d'où la
     correspondance « 3e ligne = 3e entrée de la légende ». */
  const pistesConv = React.useMemo(() => pistesConventions(occConventions), [occConventions]);
  /* POST GARDE (RQ, 03/10/2026 : « dans le planning par personne, afficher les
     PG ») : le lendemain d'une garde est le repos de garde. La case vide le DIT.
     La garde de la VEILLE DU 1er est hors du mois chargé : on la lit à part. */
  const codesGarde = React.useMemo(() => new Set(rhPostes.filter((p) => p.categorie === 'garde').map((p) => p.code)), [rhPostes]);
  const gardesDuMois = React.useMemo(() => new Set(rhAffectationsMois
    .filter((a) => codesGarde.has(String(a.poste?.code || '')) && a.personnel?.orgId)
    .map((a) => `${a.personnel?.orgId}|${a.date}`)), [rhAffectationsMois, codesGarde]);
  const veilleDuMois = React.useMemo(() => {
    const x = new Date(`${rhMois}-01T12:00:00Z`); x.setUTCDate(x.getUTCDate() - 1); return isoJour(x);
  }, [rhMois]);
  const [gardeVeilleMois, setGardeVeilleMois] = React.useState<Set<string>>(new Set());
  React.useEffect(() => {
    let vivant = true;
    void lireAffectationsDuJour(veilleDuMois)
      .then((l) => {
        if (!vivant) return;
        setGardeVeilleMois(new Set(l.filter((a) => codesGarde.has(String(a.poste?.code || ''))).map((a) => String(a.personnel?.orgId || ''))));
      })
      .catch(() => { if (vivant) setGardeVeilleMois(new Set()); });
    return () => { vivant = false; };
  }, [veilleDuMois, codesGarde, lireAffectationsDuJour]);
  const postGardeLe = (orgId: string, d: string) => {
    const x = new Date(`${d}T12:00:00Z`); x.setUTCDate(x.getUTCDate() - 1);
    const veille = isoJour(x);
    return veille === veilleDuMois ? gardeVeilleMois.has(orgId) : gardesDuMois.has(`${orgId}|${veille}`);
  };

  /* LE FILTRE PAR CATÉGORIE (RQ, 29/09/2026) — « voir qu'une partie des
     catégories de postes ». `null` = tout. Retenu dans CE navigateur : c'est un
     confort de lecture, pas une donnée. */
  const [categories, setCategories] = React.useState<string[] | null>(() => {
    try {
      const v = JSON.parse(localStorage.getItem(CLE_FILTRE) || 'null');
      return Array.isArray(v) && v.length ? v : null;
    } catch { return null; }
  });
  const choisirCategories = (v: string[] | null) => {
    setCategories(v);
    try { if (v) localStorage.setItem(CLE_FILTRE, JSON.stringify(v)); else localStorage.removeItem(CLE_FILTRE); } catch { /* navigation privée */ }
  };
  const blocDuPoste = React.useMemo(
    () => new Map(rhPostes.map((p) => [p.code, blocDeCategorie(p.categorie)])),
    [rhPostes],
  );
  const posteVisible = React.useCallback(
    (code?: string | null) => !categories || categories.includes(blocDuPoste.get(String(code || '')) || BLOC_AUTRES),
    [categories, blocDuPoste],
  );
  /* Les catégories RÉELLEMENT présentes, dans l'ordre du planning : proposer un
     filtre sur un bloc sans aucun poste ne mènerait nulle part. */
  const categoriesPresentes = React.useMemo(() => {
    const vus = new Set(rhPostes.filter((p) => p.actif).map((p) => blocDeCategorie(p.categorie)));
    return [...CATEGORIES_POSTE.map((c) => ({ cle: c.cle, libelle: c.bloc || c.libelle })), { cle: BLOC_AUTRES, libelle: 'Autres' }]
      .filter((c) => vus.has(c.cle));
  }, [rhPostes]);

  /* Un index (clé de ligne → date → affectations). Parcourir la liste pour
     chacune des ~30 × 30 cases coûterait le carré ; ici on paie une passe. */
  const parLigne = React.useMemo(() => {
    const m = new Map<string, Map<string, RhAffectation[]>>();
    for (const a of rhAffectationsMois) {
      if (!posteVisible(a.poste?.code)) continue;
      const cle = vue === 'poste' ? a.poste?.code : a.personnel?.orgId;
      if (!cle) continue;
      let jour = m.get(cle);
      if (!jour) { jour = new Map(); m.set(cle, jour); }
      const l = jour.get(a.date) || [];
      l.push(a);
      jour.set(a.date, l);
    }
    /* Dans la case, l'ordre de la JOURNÉE : le poste du matin, puis celui de
       l'après-midi, puis la garde du soir. L'ordre d'arrivée des données
       faisait passer une garde avant le poste du jour. */
    for (const jour of m.values()) {
      /* À heure égale (Accueil +++ et PLV +++ à 13:00), l'ORDRE DES POSTES
         départage, puis le code, puis le nom : sans cela, l'ordre dépendait de
         l'ordre de saisie et s'inversait d'un jour à l'autre (RQ, 30/09/2026). */
      for (const l of jour.values()) {
        l.sort((x, y) => String(x.heureDebut || '').localeCompare(String(y.heureDebut || ''))
          || (Number(x.poste?.ordre ?? 999) - Number(y.poste?.ordre ?? 999))
          || String(x.poste?.code || '').localeCompare(String(y.poste?.code || ''))
          /* Le DOUBLON après le TITULAIRE, toujours (RQ, 03/10/2026 : « Noureddine
             change de position dans le tableau ») : départagés par le nom, le
             stagiaire passait avant ou après selon le nom du titulaire du jour. */
          || Number(x.nature === 'formation') - Number(y.nature === 'formation')
          || nomDe(x.personnel).localeCompare(nomDe(y.personnel), 'fr'));
      }
    }
    return m;
  }, [rhAffectationsMois, vue, posteVisible]);

  const ouvertures = React.useMemo(() => {
    const m = new Map<string, RhOuverture>();
    for (const o of rhOuverturesMois) m.set(`${o.poste}|${o.date}`, o);
    return m;
  }, [rhOuverturesMois]);

  /* Les absences VALIDÉES seulement : une demande en attente n'est pas un
     fait, et l'afficher comme tel ferait lire un planning pour un autre. */
  const absencesPar = React.useMemo(() => {
    const m = new Map<string, RhAbsence[]>();
    for (const ab of rhAbsences) {
      if (ab.statut !== 'validee') continue;
      const id = ab.personnel?.orgId;
      if (!id) continue;
      const l = m.get(id) || [];
      l.push(ab);
      m.set(id, l);
    }
    return m;
  }, [rhAbsences]);

  const absentLe = React.useCallback((orgId: string, date: string) => {
    const l = absencesPar.get(orgId);
    if (!l) return null;
    return l.find((ab) => ab.dateDebut <= date && date <= ab.dateFin) || null;
  }, [absencesPar]);

  const postes = React.useMemo(
    () => rhPostes.filter((p) => p.actif).sort((a, b) => a.ordre - b.ordre || a.code.localeCompare(b.code)),
    [rhPostes],
  );
  /* Les abréviations des postes, DISTINCTES (29/09/2026) — tronquer à quatre
     caractères faisait de quatre accueils un seul « ACCU ». */
  const abreges = React.useMemo(() => abregesPostes(rhPostes.map((p) => p.code)), [rhPostes]);
  /* La légende ne liste que les postes TENUS ce mois-ci : trente codes dont
     la moitié n'apparaît pas dans la grille se liraient mal. */
  const postesDuMois = React.useMemo(() => {
    const vus = new Map<string, string>();
    for (const a of rhAffectationsMois) if (a.poste?.code) vus.set(a.poste.code, a.poste.libelle || a.poste.code);
    return postes.filter((p) => vus.has(p.code) && posteVisible(p.code))
      .map((p) => ({ code: p.code, libelle: vus.get(p.code) || p.libelle }));
  }, [rhAffectationsMois, postes, posteVisible]);
  const personnes = React.useMemo(
    () => effectif.filter((p) => p.type === 'salarie' && p.statut !== 'sorti')
      .sort((a, b) => a.nom.localeCompare(b.nom, 'fr')),
    [effectif],
  );

  /* ⚠️ LA COULEUR CHOISIE PRIME, l'algorithme reste le repli (Lot 2). Indexée
     sur TOUT `rhPersonnel` et non sur les seuls actifs : un mois passé affiche
     des personnes sorties depuis, et elles doivent garder leur couleur — sinon
     un planning archivé change d'apparence après un départ.
     La clé est l'`orgId` en vue « poste », le code du poste en vue « personne » :
     un poste n'a pas de couleur choisie, `couleurDe` rend alors `undefined` et
     l'algorithme reprend la main. */
  /* Les initiales RÉELLEMENT affichées : celle qui est saisie, sinon un repli
     débarrassé de ses collisions. Calculé sur TOUT l'effectif et dans l'ordre
     de la liste — le premier arrivé garde la forme courte, et deux affichages
     successifs rendent donc les mêmes pastilles. */
  /* LES PRÉNOMS AU LIEU DES INITIALES (RQ, 30/09/2026). Un prénom partagé
     par deux personnes (deux « Wafa ») reçoit l'initiale du nom : le mode
     prénoms ne doit pas réintroduire la confusion que les initiales évitaient. */
  const [enPrenoms, setEnPrenoms] = React.useState<boolean>(() => {
    try { return localStorage.getItem('bio24.planningMois.prenoms') === '1'; } catch { return false; }
  });
  const basculerPrenoms = (v: boolean) => {
    setEnPrenoms(v);
    try { localStorage.setItem('bio24.planningMois.prenoms', v ? '1' : '0'); } catch { /* navigation privée */ }
  };
  const prenomsEffectifs = React.useMemo(() => {
    const prenom = (p: RhPersonnel) => String((p as { prenom?: string | null }).prenom || '').trim().split(/\s+/)[0] || String(p.nom || '').trim();
    const compte = new Map<string, number>();
    for (const p of effectif) compte.set(prenom(p), (compte.get(prenom(p)) || 0) + 1);
    return new Map(effectif.map((p) => {
      const pr = prenom(p);
      return [String(p.orgId), (compte.get(pr) || 0) > 1 ? `${pr} ${String(p.nom || '').trim().charAt(0)}.` : pr];
    }));
  }, [effectif]);

  const initialesEffectives = React.useMemo(() => {
    const prises = new Set(effectif.map((p) => String(p.initiales || '').trim().toUpperCase()).filter(Boolean));
    const par = new Map<string, string>();
    for (const p of effectif) {
      const saisie = String(p.initiales || '').trim().toUpperCase();
      if (saisie) { par.set(String(p.orgId), saisie); continue; }
      const v = initialesSansCollision(p.nom || '', prises);
      prises.add(v);
      par.set(String(p.orgId), v);
    }
    return par;
  }, [effectif]);

  /* ⚠️ DEUX SOURCES, UNE SEULE FONCTION (05/10/2026). La clé est l'`orgId` en
     vue « poste », le CODE DU POSTE en vue « personne » (et dans les deux
     légendes). Jusqu'au 05/10/2026, seules les personnes avaient une couleur
     choisie : un code de poste ne trouvait rien, `couleurDe` rendait `null` et
     le hachage reprenait la main. Les postes en portent une désormais
     (`rh-poste.couleur`), et elle doit primer ici aussi — sinon la couleur
     réglée dans « Postes de travail » ne se verrait qu'ailleurs.
     Les personnes d'abord : les deux espaces de clés ne se recoupent pas
     (`tech.ftiti` / `P1_TRI`), et en cas d'homonymie improbable c'est la
     personne qui gagne, comme avant. */
  const couleurDe = React.useMemo(() => {
    const parPersonne = new Map(effectif.map((p) => [String(p.orgId), p.couleur || null]));
    const parPoste = new Map(rhPostes.map((p) => [String(p.code), p.couleur || null]));
    return (cle: string) => parPersonne.get(cle) || parPoste.get(cle) || null;
  }, [effectif, rhPostes]);

  /* Mémoïsé (02/09/2026) : `groupes` en dépendait par une CHAÎNE fabriquée
     (`lignes.map(l => l.cle).join('|')`) avec un `eslint-disable`, faute
     d'identité stable. Le compilateur React refuse cette forme de dépendance et
     cessait alors de mémoïser la fonction entière. On stabilise la source
     plutôt que de contourner la règle. */
  const lignes: { cle: string; titre: string; sous: string }[] = React.useMemo(
    () => (vue === 'poste'
      ? postes.filter((p) => posteVisible(p.code))
        /* LE LIBELLÉ COMPLET + L'HORAIRE (RQ, 29/09/2026) : « je vois tous
           dimanche technique ». Coupé à la largeur de la colonne, « Dimanche —
           Technique… » était identique pour quatre postes. L'horaire sous le
           code les distingue d'un coup d'œil. */
        .map((p: RhPoste) => ({
          cle: p.code, titre: p.libelle,
          sous: p.plageDebut && p.plageFin ? `${p.code} · ${p.plageDebut}–${p.plageFin}` : p.code,
          categorie: p.categorie,
        }))
      /* PRÉNOM ET NOM (RQ, 29/09/2026) — le patronyme seul ne distinguait pas
         Karima de Lamia Kouraichi. Filtré, on ne garde que les personnes qui
         tiennent au moins un poste des catégories choisies ce mois-ci. */
      : personnes
        .filter((p: RhPersonnel) => !categories || parLigne.has(p.orgId))
        .map((p: RhPersonnel) => ({ cle: p.orgId, titre: nomDe(p) || p.nom, sous: p.initiales || p.fonction, famille: familleDe(p) }))),
    [vue, postes, personnes, posteVisible, categories, parLigne],
  );

  /* LES BLOCS (RQ, 24/08/2026) — accueil, technique, validation biologique,
     qualité, entretien. En vue POSTE seulement : en vue personne les lignes
     sont des gens, et un salarié tient des postes de plusieurs blocs dans le
     même mois — le ranger dans un seul serait faux. */
  const groupes = React.useMemo(
    /* En vue PERSONNE (RQ, 01/10/2026) : par FONCTION — préparateurs,
       techniciens, biologistes, support — puis par ordre alphabétique. */
    () => (vue === 'poste' ? grouperLignes(lignes) : FAMILLES_PERSONNEL.map((f) => ({
      cle: f.cle, libelle: f.titre, note: '',
      lignes: lignes.filter((l) => (l as { famille?: string }).famille === f.cle)
        .sort((x, y) => sansTitre(x.titre).localeCompare(sansTitre(y.titre), 'fr')),
    })).filter((g) => g.lignes.length)),
    [vue, lignes],
  );

  /* Les totaux de la ligne : en vue personne c'est la charge du mois, la
     question même que la vue mensuelle existe pour poser. */
  /* ⚠️ PAS DE TOTAL EN VUE POSTE (RQ, 30/09/2026 : « le total, je ne vois pas
     à quoi il sert ») : il ne comptait que les cases remplies du poste. En vue
     PERSONNE, il reste — c'est la charge du mois de la personne. */
  const avecTotal = vue === 'personne';
  /* TROIS TOTAUX (RQ, 30/09/2026), en VACATIONS (`vacationsDuJour`) :
       · O   — ouvrés : du lundi au samedi, hors jour chômé ;
       · G   — nuits de garde ;
       · D/F — dimanches et jours chômés.
     Deux postes sur deux plages différentes font deux vacations ; sur la même
     plage, une seule. Un poste que la personne ne tient pas (absente) ne
     compte pas. */
  const chomesMois = React.useMemo(() => new Set(joursChomesDuMois(rhMois)), [joursChomesDuMois, rhMois]);
  /* ⚠️ LES TOTAUX IGNORENT LE FILTRE DE CATÉGORIES (RQ, 28/10/2026 : « quand
     j'enlève des catégories, le calcul des dim et fériés ne s'affiche plus »).
     Le filtre sert à LIRE la grille ; les dimanches et jours fériés d'une
     personne sont sa charge du mois, tous postes confondus. Index séparé de
     `parLigne` (qui, lui, suit le filtre). */
  const parPersonneTout = React.useMemo(() => {
    const m = new Map<string, Map<string, RhAffectation[]>>();
    for (const a of rhAffectationsMois) {
      const cle = a.personnel?.orgId;
      if (!cle) continue;
      let jour = m.get(cle);
      if (!jour) { jour = new Map(); m.set(cle, jour); }
      const l = jour.get(a.date) || [];
      l.push(a);
      jour.set(a.date, l);
    }
    return m;
  }, [rhAffectationsMois]);
  const totauxLigne = (cle: string) => {
    const t = { ouvres: 0, gardes: 0, dimFer: 0 };
    const jour = parPersonneTout.get(cle);
    if (!jour) return t;
    for (const d of jours) {
      const cases = (jour.get(d) || []).filter((c) => !absentLe(c.personnel?.orgId || '', d));
      if (!cases.length) continue;
      t.gardes += cases.filter((c) => c.poste?.code === 'GARDE').length;
      const v = vacationsDuJour(cases);
      if (chomesMois.has(d) || new Date(`${d}T12:00:00`).getDay() === 0) t.dimFer += v;
      else t.ouvres += v;
    }
    return t;
  };
  const COLS_TOTAL = [['ouvres', 'O', 'Ouvrés : vacations du lundi au samedi'], ['gardes', 'G', 'Gardes : nuits de garde'],
    ['dimFer', 'D/F', 'Dimanches et jours fériés : vacations']] as const;

  /* ── AUJOURD'HUI SE VOIT AUSSI AU MOIS (RQ, 05/10/2026) ───────────────────
   *
   * La vue Semaine teinte sa colonne depuis le 26/08 ; le mois, lui, n'avait
   * aucun repère — sur trente et une colonnes, retrouver le jour même se
   * faisait en comptant les cases, et c'est précisément le geste qu'une grille
   * doit épargner.
   *
   * ⚠️ UN LISERÉ, PAS UN FOND. Le fond d'une case est déjà porteur : rouge =
   * ouvert et personne, gris = fermé, gris pâle = absence ou week-end. Teinter
   * la colonne du jour aurait écrasé ces trois états le seul jour où on les
   * regarde le plus. Le repère passe donc par la BORDURE de la bande (verticale
   * si les jours sont en colonnes, horizontale s'ils sont en lignes) et par
   * l'en-tête du jour, en couleur pleine — comme la semaine.
   *
   * Calculé en LOCAL et non en UTC : `toISOString()` bascule d'un jour en fin
   * de soirée à Tunis, et le liseré aurait sauté au lendemain dès 23 h.
   */
  const auj = (() => { const n = new Date(); return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-${String(n.getDate()).padStart(2, '0')}`; })();
  /** L'en-tête du jour même — même traitement qu'en vue Semaine. */
  const TETE_AUJ: React.CSSProperties = {
    background: 'var(--color-primary-active)', color: 'var(--text-on-primary)', fontWeight: 700,
  };
  /** Le liseré de la bande du jour même, selon le sens de lecture. */
  const bandeAuj = (cejour: boolean): React.CSSProperties => (!cejour ? {} : (
    sens === 'postes-en-colonnes'
      ? { borderBlockStart: '2px solid var(--color-primary-active)', borderBlockEnd: '2px solid var(--color-primary-active)' }
      : { borderInlineStart: '2px solid var(--color-primary-active)', borderInlineEnd: '2px solid var(--color-primary-active)' }
  ));

  /* ── L'ÉCHANGE PAR GLISSER-DÉPOSER (RQ, 26/10/2026) ───────────────────────
   *
   * « Dans l'affichage par mois, je voudrais pouvoir faire un glisser-déposer ;
   *   et qu'en posant sur une autre personne, il y ait un échange entre les
   *   postes. »
   *
   * ⚠️ LE MOIS RESTE UN OUTIL DE LECTURE PAR DÉFAUT. Son en-tête le disait : un
   * mois de cases cliquables invite à planifier au kilomètre. Le glissé n'existe
   * donc que derrière un bouton « Modifier », ouvert à la main, et seulement
   * pour qui peut écrire le planning. Fermé, la pastille n'est plus qu'une
   * pastille — aucun nœud en plus, aucune prise.
   *
   * ⚠️ UN SEUL GESTE : déposer une pastille sur une autre ÉCHANGE les deux
   * postes du MÊME jour — ou, sur un MÊME poste, les deux jours (RQ,
   * 01/11/2026 : « utile pour le dimanche »). Rien n'est créé ni supprimé : chaque affectation change
   * de titulaire, côté serveur, en une transaction. D'où l'annulation —
   * rejouer le même couple le défait. Le serveur vérifie les habilitations dans
   * les deux sens ; l'écran les évalue pendant le glissé pour griser ce qui
   * serait refusé.
   */
  const echangeActif = modeEdition && rhPlanningModifiable;

  const lignesParJour = React.useMemo(() => {
    const m = new Map<string, { documentId: string; posteCode: string; orgId: string; date: string }[]>();
    for (const a of rhAffectationsMois) {
      const l = m.get(a.date) || [];
      l.push({ documentId: a.documentId, posteCode: String(a.poste?.code || ''), orgId: String(a.personnel?.orgId || ''), date: a.date });
      m.set(a.date, l);
    }
    return m;
  }, [rhAffectationsMois]);
  const postesParCode = React.useMemo(() => new Map(rhPostes.map((p) => [p.code, p])), [rhPostes]);
  const detenuesParPersonne = React.useMemo(() => {
    const par = new Map<string, Map<string, string>>();
    for (const d of rhDetenues || []) {
      const cle = String(d.orgId || '');
      if (!cle) continue;
      if (!par.has(cle)) par.set(cle, new Map());
      par.get(cle)!.set(String(d.code), String(d.niveau || ''));
    }
    return par;
  }, [rhDetenues]);
  const nomParOrg = React.useMemo(() => new Map(rhPersonnel.map((p) => [p.orgId, nomDe(p)])), [rhPersonnel]);

  const pastilleDe_ = (c: RhAffectation): PastilleEchange => ({
    documentId: c.documentId, date: c.date, posteCode: String(c.poste?.code || ''), orgId: String(c.personnel?.orgId || ''),
    nom: nomDe(c.personnel) || '?', posteLibelle: String(c.poste?.libelle || c.poste?.code || '?'),
    nature: c.nature, derogation: c.derogation, derogationAbsence: c.derogationAbsence,
  });

  const rechargerMois = React.useCallback(async () => {
    useGed.setState({ rhMoisCharge: null });
    await loadRhMois();
  }, [loadRhMois]);

  /** POST ged/rh/affectations/echanger. Rend le corps, ou `null` après avoir DIT pourquoi. */
  const appelerEchange = React.useCallback(async (idA: string, idB: string, attendu?: { a: string; b: string }) => {
    const r = await fetchEcriture('/api/ged/ged/rh/affectations/echanger', {
      method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ idA, idB, ...(attendu ? { attendu } : {}) }),
    });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) {
      /* ⚠️ 404 / 405 SANS MESSAGE : la route n'existe pas sur le serveur (API pas
         encore redémarrée après son ajout). Dire « Échange refusé » ferait chercher
         une règle de planning là où il n'y a qu'un serveur en retard. */
      showToast(d?.error?.message
        || ((r.status === 404 || r.status === 405)
          ? 'Échange indisponible : le serveur n’a pas encore cette fonction (l’API doit être redémarrée). Rien n’a été modifié.'
          : 'Échange refusé.'), 'erreur');
      /* Un 409 veut dire « le planning a bougé » : on relit, sinon l'écran
         continuerait de montrer ce que le serveur ne porte plus. */
      if (r.status === 409 || r.status === 404) await rechargerMois();
      return null;
    }
    return d as { avertissements?: string[]; a: { nom: string; nouveauPoste: string }; b: { nom: string; nouveauPoste: string }; date: string };
  }, [showToast, rechargerMois]);

  /* ── L'HISTORIQUE DU MOIS, ET « ANNULER » (RQ, 28/10/2026) ────────────────
   *
   * « Je veux un bouton pour annuler les dernières actions. » Chaque geste du
   * mode « Modifier » (échange, retrait) empile SA réciproque ; le bouton
   * dépile la dernière, et ainsi de suite.
   *
   * ⚠️ UN RETRAIT SE DÉFAIT EN RECRÉANT l'affectation : elle reçoit un NOUVEL
   * identifiant. `idActuel` suit ce renommage, pour que les entrées plus
   * anciennes — un échange sur cette même affectation — visent la bonne.
   * ⚠️ L'historique vit dans l'écran : il ne survit pas à un rechargement, et
   * ne défait pas ce qui a été fait par quelqu'un d'autre entre-temps (le
   * serveur refuse alors, et on le dit). */
  type ActionMois = { id: number; libelle: string; defaire: () => Promise<boolean> };
  const [historique, setHistorique] = React.useState<ActionMois[]>([]);
  const historiqueRef = React.useRef<ActionMois[]>([]);
  historiqueRef.current = historique;
  const [annulationEnCours, setAnnulationEnCours] = React.useState(false);
  const compteurAction = React.useRef(0);
  const renomme = React.useRef(new Map<string, string>());
  const idActuel = React.useCallback((id: string) => {
    let c = id;
    for (let n = 0; renomme.current.has(c) && n < 50; n++) c = renomme.current.get(c)!;
    return c;
  }, []);
  const empiler = React.useCallback((libelle: string, defaire: () => Promise<boolean>) => {
    const id = ++compteurAction.current;
    setHistorique((h) => [...h.slice(-29), { id, libelle, defaire }]);
    return id;
  }, []);
  const annulerAction = React.useCallback(async (id: number) => {
    const e = historiqueRef.current.find((x) => x.id === id);
    if (!e) return;
    setAnnulationEnCours(true);
    try {
      if (await e.defaire()) {
        setHistorique((h) => h.filter((x) => x.id !== id));
        showToast(`Annulé : ${e.libelle}.`);
      }
    } finally {
      setAnnulationEnCours(false);
    }
  }, [showToast]);
  const annulerDerniere = React.useCallback(() => {
    const d = historiqueRef.current[historiqueRef.current.length - 1];
    if (d) void annulerAction(d.id);
  }, [annulerAction]);

  /* Ctrl/Cmd+Z, comme partout — sauf pendant une saisie, où il appartient au champ. */
  React.useEffect(() => {
    if (!echangeActif) return;
    const h = (e: KeyboardEvent) => {
      if (!(e.ctrlKey || e.metaKey) || e.shiftKey || e.key.toLowerCase() !== 'z') return;
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable)) return;
      if (!historiqueRef.current.length) return;
      e.preventDefault();
      annulerDerniere();
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [echangeActif, annulerDerniere]);

  /** RETIRER UNE PERSONNE D'UN POSTE, d'un clic (DELETE ged/rh/affectations/:id). */
  const retirerAffectationMois = React.useCallback(async (p: PastilleEchange) => {
    const c = rhAffectationsMois.find((x) => x.documentId === p.documentId);
    const id = idActuel(p.documentId);
    const r = await fetchEcriture(`/api/ged/ged/rh/affectations/${id}`, { method: 'DELETE', credentials: 'include' });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) {
      showToast(d?.error?.message || 'Retrait refusé.', 'erreur');
      if (r.status === 404) await rechargerMois();
      return;
    }
    await Promise.all([rechargerMois(), loadMaintenances()]);
    const libelle = `retrait de ${p.nom} — « ${p.posteLibelle} » le ${p.date}`;
    const corps = {
      date: p.date, posteCode: p.posteCode, personnelOrgId: p.orgId,
      ...(c?.heureDebut ? { heureDebut: c.heureDebut } : {}), ...(c?.heureFin ? { heureFin: c.heureFin } : {}),
      ...(c?.seanceCle ? { seanceCle: c.seanceCle } : {}),
      ...(c?.nature ? { nature: c.nature } : {}),
      ...(c?.tuteurOrgId ? { tuteurOrgId: c.tuteurOrgId } : {}),
      ...(c?.note ? { note: c.note } : {}),
      ...(c?.derogation?.motif ? { derogationMotif: c.derogation.motif } : {}),
    };
    const idEntree = empiler(libelle, async () => {
      const r2 = await fetchEcriture('/api/ged/ged/rh/affectations', {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(corps),
      });
      const d2 = await r2.json().catch(() => ({}));
      if (!r2.ok) {
        showToast(`${d2?.error?.message || 'Rétablissement refusé.'} Le retrait de ${p.nom} reste en place.`, 'erreur');
        return false;
      }
      const nouveau = d2?.affectation?.documentId;
      if (nouveau) renomme.current.set(p.documentId, nouveau);
      await Promise.all([rechargerMois(), loadMaintenances()]);
      if (c?.derogationAbsence) {
        showToast(`${p.nom} est rétablie, mais sa dérogation d’absence n’est pas reprise : à reposer si besoin.`, 'indetermine');
      }
      return true;
    });
    showToast(`${p.nom} retirée de « ${p.posteLibelle} » le ${p.date}.`, 'info', {
      label: 'Annuler', faire: () => { void annulerAction(idEntree); },
    });
  }, [rhAffectationsMois, idActuel, showToast, rechargerMois, loadMaintenances, empiler, annulerAction]);

  /* ── REMPLACER LA PERSONNE D'UNE CASE (RQ, 01/11/2026) ────────────────────
   * « En plus du bouton × pour supprimer, un bouton pour placer une autre
   * personne à la place de la personne présente. » UNE route serveur
   * (`…/:id/remplacer`) : l'affectation change de titulaire sur place. Retirer
   * puis affecter d'ici ferait deux écritures — la seconde refusée, la case
   * resterait vide. L'annulation rejoue la route avec l'ancienne titulaire. */
  const [aRemplacer, setARemplacer] = React.useState<PastilleEchange | null>(null);
  const absentLeMois = React.useCallback((orgId: string, date: string) => rhAbsences.some((ab) =>
    (ab.statut === 'validee' || ab.statut === 'demandee')
    && ab.personnel?.orgId === orgId && ab.dateDebut <= date && date <= ab.dateFin), [rhAbsences]);
  const appelerRemplacement = React.useCallback(async (id: string, personnelOrgId: string, attendu?: string) => {
    const r = await fetchEcriture(`/api/ged/ged/rh/affectations/${id}/remplacer`, {
      method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ personnelOrgId, ...(attendu ? { attendu } : {}) }),
    });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) {
      showToast(d?.error?.message
        || ((r.status === 404 || r.status === 405)
          ? 'Remplacement indisponible : le serveur n’a pas encore cette fonction (l’API doit être redémarrée). Rien n’a été modifié.'
          : 'Remplacement refusé.'), 'erreur');
      if (r.status === 409) await rechargerMois();
      return null;
    }
    return d as { derogationTombee?: boolean; avertissements?: string[]; ancien: { orgId: string; nom: string }; nouveau: { orgId: string; nom: string } };
  }, [showToast, rechargerMois]);
  const remplacerPar = React.useCallback(async (p: PastilleEchange, orgId: string) => {
    const d = await appelerRemplacement(idActuel(p.documentId), orgId, p.orgId);
    if (!d) return false;
    await Promise.all([rechargerMois(), loadMaintenances()]);
    const idEntree = empiler(`remplacement de ${p.nom} par ${d.nouveau.nom} — « ${p.posteLibelle} » le ${p.date}`, async () => {
      const r = await appelerRemplacement(idActuel(p.documentId), p.orgId, orgId);
      if (!r) return false;
      await Promise.all([rechargerMois(), loadMaintenances()]);
      return true;
    });
    const notes = [
      ...(d.derogationTombee ? [`la dérogation de ${p.nom} sur cette case tombe (l’annulation ne la repose pas)`] : []),
      ...(d.avertissements || []),
    ];
    showToast(`${d.nouveau.nom} remplace ${p.nom} sur « ${p.posteLibelle} » le ${libelleJourLong(p.date)}.${notes.length ? ` ⚠ ${notes.join(' · ')}` : ''}`,
      notes.length ? 'indetermine' : 'info', { label: 'Annuler', faire: () => { void annulerAction(idEntree); } });
    return true;
  }, [appelerRemplacement, idActuel, rechargerMois, loadMaintenances, empiler, showToast, annulerAction]);

  /* ── AJOUTER QUELQU'UN SUR UNE CASE VIDE (RQ, 01/11/2026) ───────────────────
   * « Me laisser rajouter une personne sur les postes vides (vue mois). » Le «
   * + » n'apparaît qu'en mode « Modifier », en vue par poste, sur une case
   * OUVERTE sans personne présente — jamais un jour où le poste est fermé. La route est celle de la semaine (`POST
   * ged/rh/affectations`) : le serveur revérifie tout. Pas de dérogation d'ici —
   * la fenêtre ne propose que qui tient le poste ; déroger se fait en Semaine.
   * L'annulation retire l'affectation créée. */
  const [aAjouter, setAAjouter] = React.useState<{ date: string; posteCode: string; posteLibelle: string } | null>(null);
  /* Les séances du poste qui s'appliquent CE jour-là (1 = lundi … 7 = dimanche). */
  const seancesAjout = React.useMemo(() => {
    if (!aAjouter) return [];
    const js = new Date(`${aAjouter.date}T12:00:00`).getDay() || 7;
    return (postesParCode.get(aAjouter.posteCode)?.seances || []).filter((x) => !x.jours?.length || x.jours.includes(js));
  }, [aAjouter, postesParCode]);
  const [seanceAjout, setSeanceAjout] = React.useState('');
  React.useEffect(() => { setSeanceAjout(seancesAjout[0]?.cle || ''); }, [seancesAjout]);

  const ajouterSurCase = React.useCallback(async (orgId: string) => {
    if (!aAjouter) return false;
    const { date, posteCode, posteLibelle } = aAjouter;
    const r = await fetchEcriture('/api/ged/ged/rh/affectations', {
      method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ date, posteCode, personnelOrgId: orgId, ...(seancesAjout.length > 1 && seanceAjout ? { seanceCle: seanceAjout } : {}) }),
    });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) { showToast(d?.error?.message || 'Affectation refusée.', 'erreur'); return false; }
    await Promise.all([rechargerMois(), loadMaintenances()]);
    const nom = nomParOrg.get(orgId) || orgId;
    const id = String(d?.affectation?.documentId || '');
    const idEntree = id ? empiler(`ajout de ${nom} — « ${posteLibelle} » le ${date}`, async () => {
      const r2 = await fetchEcriture(`/api/ged/ged/rh/affectations/${idActuel(id)}`, { method: 'DELETE', credentials: 'include' });
      if (!r2.ok) {
        const d2 = await r2.json().catch(() => ({}));
        showToast(`${d2?.error?.message || 'Retrait refusé.'} ${nom} reste affecté(e).`, 'erreur');
        return false;
      }
      await Promise.all([rechargerMois(), loadMaintenances()]);
      return true;
    }) : null;
    showToast(`${nom} affecté(e) à « ${posteLibelle} » le ${libelleJourLong(date)}.${d?.alerte ? ` ⚠ ${d.alerte}` : ''}`,
      d?.alerte ? 'indetermine' : 'info',
      idEntree ? { label: 'Annuler', faire: () => { void annulerAction(idEntree); } } : undefined);
    return true;
  }, [aAjouter, seancesAjout, seanceAjout, showToast, rechargerMois, loadMaintenances, nomParOrg, empiler, idActuel, annulerAction]);

  /* Les candidates à une case vide : même classement, même règle que le
     remplacement — une case « fantôme » sans titulaire. */
  const candidatsAjout = React.useMemo((): CandidatRemplacement[] => {
    if (!aAjouter) return [];
    const fantome = { documentId: '', date: aAjouter.date, posteCode: aAjouter.posteCode, orgId: '', nature: 'travail' };
    const lignes = lignesParJour.get(aAjouter.date) || [];
    const poste = postesParCode.get(aAjouter.posteCode);
    return personnes.map((x) => {
      const orgId = String(x.orgId);
      const nom = nomDe(x) || orgId;
      const refus = refusRemplacement(fantome, orgId, lignes, absentLeMois);
      if (refus) return { orgId, nom, ok: false, motif: refus };
      if (poste && detenuesParPersonne.size) {
        const v = verdictLocal({ orgId }, poste, detenuesParPersonne);
        if (!v.ok) {
          return { orgId, nom, ok: false,
            motif: `Pas habilité(e) sur ce poste — il manque : ${[...v.manquantes, ...v.enFormation.map((c) => `${c} (en formation)`)].join(', ')}. Une dérogation se pose en vue Semaine.` };
        }
      }
      const deja = rhAffectationsMois
        .filter((a) => a.date === aAjouter.date && String(a.personnel?.orgId || '') === orgId)
        .map((a) => `« ${a.poste?.libelle || a.poste?.code || '?'} »`);
      return { orgId, nom, ok: true, dejaCeJour: deja };
    });
  }, [aAjouter, lignesParJour, postesParCode, personnes, absentLeMois, detenuesParPersonne, rhAffectationsMois]);

  /* Les candidates à la case ouverte : toutes les salariées présentes, chacune
     avec son verdict — la même règle que le serveur (`refusRemplacement`, puis
     les habilitations). Habilitations non chargées : on ne bloque pas. */
  const candidatsRemplacement = React.useMemo((): CandidatRemplacement[] => {
    if (!aRemplacer) return [];
    const p = aRemplacer;
    const lignes = lignesParJour.get(p.date) || [];
    const poste = postesParCode.get(p.posteCode);
    return personnes.filter((x) => String(x.orgId) !== p.orgId).map((x) => {
      const orgId = String(x.orgId);
      const nom = nomDe(x) || orgId;
      const refus = refusRemplacement(p, orgId, lignes, absentLeMois);
      if (refus) return { orgId, nom, ok: false, motif: refus };
      if (poste && detenuesParPersonne.size) {
        const v = verdictLocal({ orgId }, poste, detenuesParPersonne);
        if (!v.ok) {
          return { orgId, nom, ok: false,
            motif: `Pas habilité(e) sur ce poste — il manque : ${[...v.manquantes, ...v.enFormation.map((c) => `${c} (en formation)`)].join(', ')}.` };
        }
      }
      const deja = rhAffectationsMois
        .filter((a) => a.date === p.date && String(a.personnel?.orgId || '') === orgId && a.documentId !== p.documentId)
        .map((a) => `« ${a.poste?.libelle || a.poste?.code || '?'} »`);
      return { orgId, nom, ok: true, dejaCeJour: deja };
    });
  }, [aRemplacer, lignesParJour, postesParCode, personnes, absentLeMois, detenuesParPersonne, rhAffectationsMois]);

  const contexteEchange = React.useMemo(() => ({
    actif: echangeActif,
    /* Les lignes des DEUX jours : d'un jour à l'autre (même poste — RQ,
       01/11/2026, « utile pour le dimanche »), le doublon se juge à la date
       que chacune prend. Les absences comptent validées OU demandées, comme
       au serveur. */
    valide: (a: PastilleEchange, b: PastilleEchange) => echangePossible(
      a, b,
      a.date === b.date ? lignesParJour.get(a.date) || [] : [...(lignesParJour.get(a.date) || []), ...(lignesParJour.get(b.date) || [])],
      postesParCode, detenuesParPersonne,
      (o) => nomParOrg.get(o) || o,
      (orgId, date) => rhAbsences.some((ab) => (ab.statut === 'validee' || ab.statut === 'demandee')
        && ab.personnel?.orgId === orgId && ab.dateDebut <= date && date <= ab.dateFin),
    ),
    refuser: (motif: string) => showToast(motif, 'erreur'),
    echanger: async (a: PastilleEchange, b: PastilleEchange) => {
      const d = await appelerEchange(idActuel(a.documentId), idActuel(b.documentId), { a: a.orgId, b: b.orgId });
      if (!d) return;
      await rechargerMois();
      const [libA, libB] = [a.posteLibelle, b.posteLibelle];
      /* L'ANNULATION : l'échange est sa propre réciproque, on rejoue le même
         couple. Pas de `attendu` — c'est NOTRE geste qu'on défait. */
      const memeJour = a.date === b.date;
      const idEntree = empiler(`échange ${a.nom} ⇄ ${b.nom} (${memeJour ? a.date : `${a.date} / ${b.date}`})`, async () => {
        const r = await appelerEchange(idActuel(a.documentId), idActuel(b.documentId));
        if (!r) return false;
        await rechargerMois();
        return true;
      });
      showToast(
        `Échangé : ${memeJour
          ? `${a.nom} → ${libB}, ${b.nom} → ${libA}`
          : `« ${libA} », ${a.nom} prend le ${libelleJourLong(b.date)}, ${b.nom} le ${libelleJourLong(a.date)}`}.${d.avertissements?.length ? ` ⚠ ${d.avertissements.join(' ')}` : ''}`,
        d.avertissements?.length ? 'indetermine' : 'info',
        { label: 'Annuler', faire: () => { void annulerAction(idEntree); } },
      );
    },
  }), [echangeActif, lignesParJour, postesParCode, detenuesParPersonne, nomParOrg, rhAbsences, showToast, appelerEchange, rechargerMois, idActuel, empiler, annulerAction]);

  /* LA CELLULE, UNE FOIS POUR LES DEUX SENS DE LECTURE.
     Le tableau se lit désormais dans les deux sens — postes en lignes, ou
     postes en colonnes (RQ, 29/08/2026, pour retrouver la forme du tableau
     Excel du laboratoire). Dupliquer le rendu de la case aurait fait diverger
     les deux vues au premier correctif : le trou de couverture, la dérogation
     cerclée d'ambre, le jour fermé sont ce que cette vue existe pour montrer,
     et ils doivent se voir à l'identique dans les deux sens. */
  const cellule = (l: { cle: string; titre: string; sous: string }, d: string) => {
    const cases = parLigne.get(l.cle)?.get(d) || [];
    const js = new Date(`${d}T00:00:00`).getDay();
    const weekend = js === 0 || js === 6;

    /* VUE POSTE — l'état d'ouverture prime sur le vide : une
       case vide un jour fermé est normale, la même un jour
       ouvert est un trou. Les confondre est l'erreur que cette
       vue existe pour éviter. */
    const o = vue === 'poste' ? ouvertures.get(`${l.cle}|${d}`) : undefined;
    const ferme = o?.ouverture.etat === 'ferme';
    /* ⚠️ POSTÉ MAIS PERSONNE N'EST VENU (RQ, 29/09/2026) : en vue POSTE, une
       case dont TOUTES les personnes postées sont absentes se lit comme un
       poste vide — fond rouge et point rouge — tout en gardant les initiales
       barrées : on voit le trou ET qui devait le tenir. */
    const tousAbsents = vue === 'poste' && cases.length > 0
      && cases.every((c) => !!absentLe(c.personnel?.orgId || '', d));
    const trou = (!!o && o.ouverture.etat === 'ouvert' && cases.length === 0) || tousAbsents;

    /* VUE PERSONNE — l'absence validée explique un vide. */
    const ab = vue === 'personne' ? absentLe(l.cle, d) : null;
    const pg = vue === 'personne' && !cases.length && !ab && postGardeLe(l.cle, d);

    const fond = ferme ? 'var(--gray-100)'
      : trou ? 'var(--red-50)'
        : ab ? 'var(--gray-50)'
          : weekend ? 'var(--gray-25)' : undefined;

    const titre = ferme ? `${d} — fermé${o?.ouverture.motif ? ` (${o.ouverture.motif})` : ''}`
      : tousAbsents ? `${d} — non tenu : ${cases.map((c) => `${nomDe(c.personnel) || '?'} (${absentLe(c.personnel?.orgId || '', d)?.motifLabel || 'absent(e)'})`).join(', ')}`
      : trou ? `${d} — ouvert et non couvert${o?.manque ? ` (${o.manque} manquant)` : ''}`
        : ab ? `${d} — ${ab.motifLabel || 'absent'}${cases.length ? ` · planifié(e) : ${cases.map((c) => c.poste?.libelle || c.poste?.code || '?').join(', ')}` : ''}`
          : pg ? `${d} — post garde (repos après la garde de la veille)`
          : cases.length
            ? `${d} — ${cases.map((c) => (vue === 'poste'
              ? `${nomDe(c.personnel) || '?'} ${c.heureDebut || ''}–${c.heureFin || ''}`
              : `${c.poste?.libelle || '?'} ${c.heureDebut || ''}–${c.heureFin || ''}`)).join(' · ')}`
            : d;

    return (
      <td key={d} title={titre} style={{ ...cellBase, background: fond, ...bandeAuj(d === auj) }}>
        {cases.length ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'stretch', padding: 1 }}>
            {cases.map((c) => {
              const cle = vue === 'poste' ? (c.personnel?.orgId || '?') : (c.poste?.code || '?');
              /* ⚠️ LE REPLI RÉSOUT SES COLLISIONS (02/09/2026). Il prenait
                 les deux premiers caractères du NOM — « Me » pour Mehdi Ben
                 Salem comme pour Mezri Belhadj Amor. Deux pastilles identiques
                 dans une grille où l'on ne lit que deux lettres, et rien ne le
                 signalait. `initialesEffectives` est calculé une fois pour tout
                 l'effectif, dans un ordre stable. */
              const texte = vue === 'poste'
                ? (enPrenoms
                  ? (prenomsEffectifs.get(c.personnel?.orgId || '') || initialesEffectives.get(c.personnel?.orgId || '') || '?')
                  : (initialesEffectives.get(c.personnel?.orgId || '') || '?'))
                : (abreges.get(c.poste?.code || '') || abregePoste(c.poste?.code || '?'));
              const p = pastilleDe(cle, couleurDe(cle));
              /* ⚠️ POSTÉ MAIS ABSENT (RQ, 29/09/2026 — décision finale) : on
                 garde la personne à sa place, BARRÉE, avec une petite note.
                 Retirer l'affectation faisait perdre qui devait tenir le
                 poste ; la montrer telle quelle faisait croire qu'elle l'avait
                 tenu. */
              const absente = vue === 'poste' ? absentLe(c.personnel?.orgId || '', d) : ab;
              /* Suivre une personne : en vue Poste la pastille EST la personne ; en
                 vue Salarié c'est la ligne entière (son nom en tête de ligne). */
              const attenue = !!suivi && (vue === 'poste' ? cle !== suivi : l.cle !== suivi);
              const suivable = vue === 'poste' && !echangeActif;
              return (
                <PastilleEchangeable key={c.documentId} a={pastilleDe_(c)} onRetirer={retirerAffectationMois} onRemplacer={setARemplacer}>
                <span className={`${absente ? 'pm-pastille pm-absente' : 'pm-pastille'}${attenue ? ' pm-attenue' : ''}`}
                  onClick={suivable ? () => basculerSuivi(cle) : undefined}
                  style={{
                  ...p, border: `1px solid ${p.borderColor}`, borderRadius: 'var(--radius-xs)',
                  transition: 'opacity .15s ease-out',
                  ...(suivable ? { cursor: 'pointer' } : {}),
                  ...(attenue ? { opacity: 0.18 } : {}),
                  fontSize: 9, fontWeight: 700, lineHeight: '15px', letterSpacing: '0.02em',
                  ...(enPrenoms && vue === 'poste' ? { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', letterSpacing: 0, padding: '0 2px' } : {}),
                  /* La dérogation ne se cache pas, même dans une
                     pastille de neuf pixels : elle est le seul
                     cas où quelqu'un tient un poste qu'il n'est
                     pas habilité à tenir. */
                  outline: c.derogation ? '1.5px solid var(--amber-500)' : undefined,
                  ...(absente ? { opacity: attenue ? 0.18 : 0.45, textDecoration: 'line-through' } : {}),
                }} title={absente ? `${vue === 'poste' ? `${nomDe(c.personnel)} — ` : ''}${absente.motifLabel || 'absent(e)'}` : (suivable ? 'Cliquer pour suivre cette personne sur le mois' : undefined)}>{texte}</span>
                </PastilleEchangeable>
              );
            })}
            {ab && <span className="pm-abs" style={{ color: 'var(--text-muted)', fontSize: 8.5, lineHeight: 1 }} aria-label={ab.motifLabel || 'absent'}>abs</span>}
          </div>
        ) : ab ? (
          <span className="pm-abs" style={{ color: 'var(--text-muted)', fontSize: 9 }} aria-label={ab.motifLabel || 'absent'}>abs</span>
        ) : pg ? (
          <span style={{ color: 'var(--navy-700)', fontWeight: 700, fontSize: 9 }} aria-label="post garde">PG</span>
        ) : trou ? (
          <span style={{ color: 'var(--red-600)', fontWeight: 700 }} aria-label="ouvert et non couvert">•</span>
        ) : null}
        {tousAbsents && (
          <span style={{ display: 'block', color: 'var(--red-600)', fontWeight: 700, lineHeight: 1 }} aria-label="personne présente : le poste n’est pas tenu">•</span>
        )}
        {/* « + » : ajouter quelqu'un sur une case vide (RQ, 01/11/2026). Mode
            « Modifier », vue par poste, personne de présent. ⚠️ JAMAIS UN JOUR
            OÙ LE POSTE EST FERMÉ (RQ, 01/11/2026 : « je ne veux pas de plus quand
            le poste n'est pas du tout ouvert ce jour-là ») — essayé, puis retiré. */}
        {vue === 'poste' && echangeActif && !ferme && (!cases.length || tousAbsents) && (
          <button type="button" className="pm-ecran"
            aria-label={`Ajouter une personne sur « ${l.titre} » le ${d}`}
            title={`Ajouter une personne sur « ${l.titre} » le ${libelleJourLong(d)}`}
            onClick={() => setAAjouter({ date: d, posteCode: l.cle, posteLibelle: l.titre })}
            style={{
              display: 'block', margin: '1px auto', width: 16, height: 16, padding: 0, lineHeight: '14px',
              fontSize: 12, fontWeight: 700, cursor: 'pointer', border: '1px dashed var(--color-primary)',
              borderRadius: 'var(--radius-xs)', background: 'var(--surface-card)', color: 'var(--color-primary)',
            }}>+</button>
        )}
      </td>
    );
  };

  /* LE SENS DE LECTURE (RQ, 29/08/2026) — « je veux pouvoir voir le planning
     avec les postes en haut et les jours à la verticale ».
     C'est la forme du tableau Excel que le laboratoire tient depuis des années :
     une ligne par jour, une colonne par poste. On ne REMPLACE pas l'autre sens,
     on l'ajoute — lire le mois d'UN poste (repérer une rotation arrêtée) et lire
     la journée de TOUS les postes (repérer un trou) sont deux questions
     différentes, et chacune a son sens de lecture. */
  /* Le sens vient du PARENT depuis le 30/08/2026 : le bouton est remonté à
     côté du sélecteur Semaine/Mois pour se voir quel que soit l'onglet. */

  /* ── CE QUI DEMANDE UN REGARD (RQ, 30/10/2026 : « c'est bien, mais pas assez ») ──
   *
   * Les trous et les absences contradictoires existaient déjà dans la grille,
   * sous forme d'un point rouge ou d'initiales barrées. Les trouver voulait dire
   * balayer trente et un jours. Le panneau ci-dessous les LISTE, et chaque ligne
   * ouvre la journée concernée. Il suit le filtre de catégories : on y lit ce
   * que la grille montre. */
  const pointsMois = React.useMemo(() => pointsAttention({
    jours,
    postes: postes.filter((p) => posteVisible(p.code)).map((p) => ({ code: p.code, libelle: p.libelle })),
    ouverture: (code, d) => {
      const o = ouvertures.get(`${code}|${d}`);
      return o ? { etat: o.ouverture.etat, manque: o.manque } : null;
    },
    affectations: rhAffectationsMois.map((x) => ({
      date: x.date, posteCode: String(x.poste?.code || ''), orgId: String(x.personnel?.orgId || ''), nom: nomDe(x.personnel) || '?',
    })).filter((x) => x.posteCode && x.orgId && posteVisible(x.posteCode)),
    absence: (orgId, d) => { const ab = absentLe(orgId, d); return ab ? (ab.motifLabel || 'absent(e)') : null; },
  }), [jours, postes, posteVisible, ouvertures, rhAffectationsMois, absentLe]);
  const resumeMois = React.useMemo(() => resumeAttention(pointsMois), [pointsMois]);
  const [attentionOuverte, setAttentionOuverte] = React.useState(false);
  const [avecPasses, setAvecPasses] = React.useState(false);

  /* SUIVRE UNE PERSONNE (RQ, 30/10/2026) : sur un mois dense, retrouver « où est
     Aya ? » voulait dire lire trente et une colonnes. Un clic sur sa pastille,
     sur son nom (vue Salarié) ou sur son badge de la légende estompe les autres.
     ⚠️ Estompé À L'ÉCRAN seulement : `pm-attenue` est neutralisé à l'impression. */
  const [suivi, setSuivi] = React.useState<string | null>(null);
  const basculerSuivi = (orgId: string) => setSuivi((v) => (v === orgId ? null : orgId));
  React.useEffect(() => {
    if (!suivi) return;
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') setSuivi(null); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [suivi]);

  const [a, m] = rhMois.split('-').map(Number);
  const enCours = rhChargement > 0 && rhMoisCharge !== rhMois;

  /* ⚠️ UN REFUS QUI NE DIT RIEN — `return null` rendait une page BLANCHE sous
     le sélecteur Semaine/Mois : ni grille, ni phrase, ni refus. Le lecteur
     concluait à une panne, ou pire, à un mois sans personne. On rend
     désormais, et on ne refuse que ce que le moteur refuse. */
  if (!peutLire) {
    return (
      <EmptyState
        titre="La vue mensuelle du planning ne vous est pas ouverte"
        indice="Le planning se lit largement, mais votre profil n’a pas la rubrique « Planning ». Le responsable qualité peut l’ouvrir dans Paramétrage → Rôles & accès." />
    );
  }

  return (
    <section className="planning-mois" ref={sectionRef}>
      <EchangeContext ctx={contexteEchange}>
      {/* IMPRESSION A4 — trente et une colonnes ne tiennent qu'en paysage, et
          seulement réduites. Les commandes disparaissent : une feuille affichée
          en salle n'a pas de boutons. */}
      <style>{`@media print { ${IMPRESSION('')} } ${CAPTURE}`}</style>

      {/* ── LA BARRE DU MOIS N'EXISTE PLUS (RQ, 26/10/2026) ─────────────────────
          La navigation, l'impression et les réglages sont dans la barre COMMUNE
          de l'écran — la même que la semaine et le jour. Ne reste ici que ce qui
          est PROPRE au mois : son commentaire, et un titre qui ne s'affiche que
          sur la FEUILLE (impression, PDF) — à l'écran, le titre est dans la barre. */}
      <div className="pm-barre" style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '0 0 12px', flexWrap: 'wrap' }}>
        <strong className="pm-titre-feuille" style={{ display: 'none' }}>{MOIS_NOMS[m - 1]} {a}</strong>
        {/* Réservé au rôle `rh` (RQ, 03/10/2026) : ni la case, ni le texte imprimé n'existent pour les autres. */}
        {peutVoirCommentairePlanning(session.sections) && (
          <CommentaireMois key={rhMois} mois={rhMois} modifiable={peutEcrireCommentairePlanning(session.sections)} />
        )}
      </div>

      {/* Les COMMANDES du mois, posées dans la barre de l'écran. */}
      {emplacements?.actions && createPortal(
        <>
          <Bouton taille="sm" type="button" disabled={pdfEnCours} onClick={() => void exporterPdf()} title="Génère un PDF A4 paysage, toujours à l'endroit, prêt à imprimer">
            <I n="download" s={14} /> {pdfEnCours ? 'PDF en cours…' : 'PDF (A4 paysage)'}
          </Bouton>
          <Bouton taille="sm" type="button" onClick={() => window.print()}>
            <I n="print" s={14} /> Imprimer
          </Bouton>
        </>, emplacements.actions)}
      {emplacements?.affichage && vue === 'poste' && createPortal(
        <span role="group" aria-label="Affichage des personnes" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <Segment ariaLabel="Affichage des personnes" valeur={enPrenoms ? 'prenoms' : 'initiales'} onChange={(v) => basculerPrenoms(v === 'prenoms')}
            options={[{ valeur: 'initiales' as const, libelle: 'Initiales' }, { valeur: 'prenoms' as const, libelle: 'Prénoms' }]} />
        </span>, emplacements.affichage)}

      {echangeActif && (
        <p role="status" className="pm-ecran" style={{
          margin: '0 0 10px', padding: '8px 12px', fontSize: 12.5, borderRadius: 'var(--radius-sm)',
          background: 'var(--cyan-50)', border: '1px solid var(--color-primary)',
        }}>
          <strong>Mode modification.</strong> Glissez une pastille sur une autre pour <strong>échanger les deux postes du même jour</strong>, ou <strong>deux jours d’un même poste</strong> (deux dimanches, par exemple).
          Les pastilles qui acceptent l’échange s’entourent dès la prise ; les autres s’estompent. Pour <strong>retirer</strong> une personne d’un poste,
          cliquez sur la croix qui apparaît sur sa pastille ; pour <strong>la remplacer</strong> par une autre, sur le <I n="refresh" s={11} style={{ display: 'inline', verticalAlign: '-1px' }} /> du coin opposé ; sur une case vide, le <strong>+</strong> ajoute quelqu’un. Tout se défait avec « Annuler » (ou Ctrl+Z).
          <Bouton taille="sm" type="button" disabled={!historique.length || annulationEnCours} onClick={annulerDerniere}
            title={historique.length ? `Annuler : ${historique[historique.length - 1].libelle}` : 'Aucune action à annuler'}
            style={{ marginInlineStart: 10 }}>
            <I n="back" s={13} /> Annuler{historique.length ? ` (${historique.length})` : ''}
          </Bouton>
        </p>
      )}

      {categoriesPresentes.length > 1 && (
        <div role="group" aria-label="Catégories de postes affichées" className="pm-ecran"
          style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', margin: '0 0 10px' }}>
          <strong style={{ fontSize: 12.5, marginInlineEnd: 2 }}>Catégories :</strong>
          <Bouton variante={!categories ? 'primaire' : 'contour'} taille="sm" type="button" aria-pressed={!categories} onClick={() => choisirCategories(null)}>Toutes</Bouton>
          {categoriesPresentes.map((c) => {
            const actif = !!categories && categories.includes(c.cle);
            return (
              <Bouton variante={actif ? 'primaire' : 'contour'} taille="sm" key={c.cle} type="button" aria-pressed={actif} onClick={() => {
                  const base = categories || [];
                  const n = actif ? base.filter((x) => x !== c.cle) : [...base, c.cle];
                  choisirCategories(n.length ? n : null);
                }}>
                {c.libelle}
              </Bouton>
            );
          })}
          {categories && (
            <span style={{ fontSize: 12, color: 'var(--amber-600)' }}>
              Filtré : {categories.length} catégorie(s) sur {categoriesPresentes.length} — les totaux O / G / D/F comptent tout le mois, toutes catégories.
            </span>
          )}
        </div>
      )}

      {/* ── CE QUI DEMANDE UN REGARD ET QUI EST SUIVI ─────────────────────────
          Une ligne quand tout va bien (le silence se DIT : « rien à signaler »),
          un résumé cliquable sinon. Jamais en rouge plein : c'est un outil de
          lecture, pas une alarme. */}
      {!enCours && lignes.length > 0 && (() => {
        const aVenir = pointsMois.filter((p) => p.date >= auj);
        const passes = pointsMois.length - aVenir.length;
        const montres = avecPasses ? pointsMois : aVenir;
        const nomSuivi = suivi ? (nomParOrg.get(suivi) || suivi) : null;
        return (
          <div className="pm-ecran" style={{ margin: '0 0 10px', display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', fontSize: 12.5 }}>
              {pointsMois.length === 0 ? (
                <span style={{ color: 'var(--text-muted)' }}>
                  <I n="check" s={13} /> Aucun poste ouvert sans personne, aucune absence contradictoire ce mois-ci.
                </span>
              ) : (
                <Bouton taille="sm" variante="contour" type="button" aria-expanded={attentionOuverte} onClick={() => setAttentionOuverte((v) => !v)}
                  title="Liste des postes ouverts sans personne et des personnes postées pendant une absence">
                  <I n="alert-triangle" s={13} /> À vérifier :{' '}
                  {[
                    resumeMois.nonTenus ? `${resumeMois.nonTenus} poste${resumeMois.nonTenus > 1 ? 's' : ''} non tenu${resumeMois.nonTenus > 1 ? 's' : ''}` : '',
                    resumeMois.surAbsence ? `${resumeMois.surAbsence} personne${resumeMois.surAbsence > 1 ? 's' : ''} postée${resumeMois.surAbsence > 1 ? 's' : ''} sur absence` : '',
                  ].filter(Boolean).join(' · ')}
                  <I n={attentionOuverte ? 'chevron-bas' : 'chevron-right'} s={13} />
                </Bouton>
              )}
              {nomSuivi && (
                <span role="status" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: 'var(--text-body)' }}>
                  Vous suivez <strong>{nomSuivi}</strong>
                  <Bouton taille="sm" variante="contour" type="button" onClick={() => setSuivi(null)} title="Revenir à toute la grille (Échap)">
                    <I n="x" s={12} /> Arrêter
                  </Bouton>
                </span>
              )}
              {!nomSuivi && !echangeActif && vue === 'poste' && (
                <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>Astuce : cliquez sur une pastille pour suivre une personne sur le mois.</span>
              )}
            </div>
            {pointsMois.length > 0 && attentionOuverte && (
              <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
                {montres.length === 0 && (
                  <p style={{ margin: 0, padding: '10px 12px', fontSize: 12.5, color: 'var(--text-muted)' }}>
                    Rien à signaler pour les jours à venir.
                  </p>
                )}
                <ul style={{ listStyle: 'none', margin: 0, padding: 0, maxHeight: 260, overflowY: 'auto' }}>
                  {montres.map((p) => (
                    <li key={`${p.date}|${p.posteCode}`} style={{ borderBottom: '1px solid var(--border-default)' }}>
                      <button type="button" onClick={() => setJourOuvert(p.date)}
                        title={`Ouvrir la journée du ${libelleJourLong(p.date)}`}
                        style={{ display: 'flex', gap: 12, alignItems: 'baseline', width: '100%', textAlign: 'start', background: 'none', border: 'none', cursor: 'pointer',
                          padding: '6px 12px', font: 'inherit', fontSize: 12.5, color: 'var(--text-body)' }}>
                        <span style={{ minWidth: 120, fontWeight: 600, fontVariantNumeric: 'tabular-nums', color: p.date < auj ? 'var(--text-muted)' : undefined }}>{libelleJourLong(p.date)}</span>
                        <span style={{ minWidth: 150 }}>{p.posteLibelle}</span>
                        <span style={{ color: p.genre === 'absent' ? 'var(--amber-600)' : 'var(--red-600)' }}>{p.detail}</span>
                      </button>
                    </li>
                  ))}
                </ul>
                {passes > 0 && (
                  <div style={{ padding: '6px 12px' }}>
                    <Bouton taille="sm" variante="contour" type="button" onClick={() => setAvecPasses((v) => !v)}>
                      {avecPasses ? 'Masquer les jours passés' : `Voir aussi ${passes} point${passes > 1 ? 's' : ''} sur des jours passés`}
                    </Bouton>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })()}

      {enCours && !rhAffectationsMois.length ? (
        <div style={{ ...card, padding: 0, overflow: 'hidden' }}><Skeleton rows={8} /></div>
      ) : !lignes.length ? (
        <EmptyState
          titre={vue === 'poste' ? 'Aucun poste actif' : 'Aucun salarié planifiable'}
          indice="La vue mensuelle relit ce que la semaine a planifié : il faut d’abord des postes et des personnes."
        />
      ) : sens === 'postes-en-colonnes' ? (
        <div className="mois-defile" style={{ ...card, padding: 0, overflowX: 'auto' }}>
          <table className="ged-tableau" ref={enTeteCollant} style={{ borderCollapse: 'collapse', width: '100%', minWidth: 940, tableLayout: 'fixed' }}>
            <colgroup>
              <col className="pm-col-jour-tete" style={{ width: 78 }} />
              {groupes.flatMap((g) => g.lignes).map((l) => <col key={l.cle} />)}
            </colgroup>
            <thead>
              {/* Deux étages d'en-tête : le BLOC au-dessus, le poste en dessous.
                  Sans l'étage du bloc, vingt-cinq codes se suivraient sans que
                  rien ne dise où finit l'accueil et où commence la technique —
                  c'est la séparation que la RQ a demandée le 24/08. */}
              {groupes.some((g) => g.libelle) ? (
                <tr>
                  <th scope="col" style={{ ...cellBase, background: 'var(--gray-25)', position: 'sticky', insetInlineStart: 0, zIndex: 2 }} />
                  {groupes.map((g) => (
                    <th scope="col" key={g.cle} colSpan={g.lignes.length} title={g.note || undefined}
                      style={{ ...cellBase, background: 'var(--gray-50)', fontSize: 'var(--tableau-entete-compacte-police)',
                        fontWeight: 700, letterSpacing: '.04em', textTransform: 'uppercase',
                        color: 'var(--text-muted)', padding: 'var(--tableau-ligne-compacte-padding-y) var(--tableau-ligne-compacte-padding-x)' }}>
                      {g.libelle}
                    </th>
                  ))}
                </tr>
              ) : null}
              <tr>
                <th scope="col" style={{ ...cellBase, textAlign: 'start', padding: 'var(--tableau-ligne-compacte-padding-y) var(--tableau-ligne-compacte-padding-x)', background: 'var(--gray-25)', position: 'sticky', insetInlineStart: 0, zIndex: 2, fontSize: 'var(--tableau-entete-compacte-police)' }}>
                  Jour
                </th>
                {groupes.flatMap((g) => g.lignes).map((l) => (
                  <th scope="col" key={l.cle} title={`${l.titre} — ${l.sous}`}
                    style={{ ...cellBase, background: 'var(--gray-25)', fontSize: 'var(--tableau-entete-compacte-police)', lineHeight: 1.15, padding: 'var(--tableau-ligne-compacte-padding-y) var(--tableau-ligne-compacte-padding-x)' }}>
                    {/* Le CODE et non le libellé : « P3 — Biochimie » ne tient pas
                        dans une colonne de trente pixels, et tronqué il ne
                        distingue plus P3 de P4. Le libellé complet est au survol. */}
                    <div style={{ fontFamily: 'var(--font-mono)', fontWeight: 700 }}>{l.cle}</div>
                    {/* Le libellé, en petit et sur plusieurs lignes (29/09/2026) :
                        le code seul ne distinguait pas les postes du dimanche. */}
                    <div style={{ fontWeight: 400, fontSize: 8.5, color: 'var(--text-muted)', whiteSpace: 'normal', overflowWrap: 'anywhere' }}>
                      {l.titre.replace(/^Dim(anche)?\s*[—–-]\s*/, '')}
                    </div>
                    {l.sous.includes('·') && (
                      <div style={{ fontWeight: 400, fontSize: 8.5, color: 'var(--text-muted)', fontVariantNumeric: 'tabular-nums' }}>
                        {l.sous.split('·')[1]?.trim()}
                      </div>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {jours.map((d) => {
                const js = new Date(`${d}T00:00:00`).getDay();
                const weekend = js === 0 || js === 6;
                return (
                  <tr key={d}>
                    <th scope="row" aria-current={d === auj ? 'date' : undefined}
                      title={d === auj ? 'Aujourd’hui' : undefined}
                      style={{ ...cellBase, textAlign: 'start', padding: '3px 8px',
                      background: weekend ? 'var(--gray-50)' : 'var(--surface-raised)',
                      position: 'sticky', insetInlineStart: 0, zIndex: 1, fontWeight: 600, fontSize: 11.5,
                      ...bandeAuj(d === auj), ...(d === auj ? TETE_AUJ : {}) }}>
                      {/* ⚠️ L'EN-TÊTE DU JOUR EST LE BOUTON, PAS LA CASE : une
                          case est un croisement poste × jour, et la rendre
                          cliquable aurait volé le clic aux pastilles. */}
                      <button type="button" className="pm-jour" onClick={() => setJourOuvert(d)}
                        aria-label={`Voir la journée du ${libelleJourLong(d)}`}
                        title={`Voir la journée du ${libelleJourLong(d)}`}
                        style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', font: 'inherit', color: 'inherit', textDecoration: 'underline', textDecorationStyle: 'dotted', textUnderlineOffset: 2 }}>
                        <span style={{ color: d === auj ? 'var(--surface-card)' : 'var(--text-muted)', fontWeight: 400 }}>{JOURS_COURTS[js]}</span>
                        {' '}
                        <span style={{ fontVariantNumeric: 'tabular-nums' }}>{Number(d.slice(8))}</span>
                      </button>
                    </th>
                    {groupes.flatMap((g) => g.lignes).map((l) => cellule(l, d))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="mois-defile" style={{ ...card, padding: 0, overflowX: 'auto' }}>
          <table className="ged-tableau" ref={enTeteCollant} style={{ borderCollapse: 'collapse', width: '100%',
            /* En mode prénoms, une colonne de jour doit pouvoir porter « Abderraouf » :
               on élargit le tableau, qui défile, plutôt que de tronquer. */
            minWidth: enPrenoms && vue === 'poste' ? 180 + jours.length * 64 : 940, tableLayout: 'fixed' }}>
            <colgroup>
              <col className="pm-col-tete" style={{ width: 168 }} />
              {jours.map((d) => <col key={d} />)}
              {avecTotal && COLS_TOTAL.map(([k]) => <col key={k} className="pm-col-total" style={{ width: 28 }} />)}
            </colgroup>
            <thead>
              <tr>
                <th scope="col" style={{ ...cellBase, textAlign: 'start', padding: 'var(--tableau-ligne-compacte-padding-y) var(--tableau-ligne-compacte-padding-x)', background: 'var(--gray-25)', position: 'sticky', insetInlineStart: 0, zIndex: 1, fontSize: 'var(--tableau-entete-compacte-police)' }}>
                  {vue === 'poste' ? 'Poste' : 'Salarié'}
                </th>
                {jours.map((d) => {
                  const js = new Date(`${d}T00:00:00`).getDay();
                  const weekend = js === 0 || js === 6;
                  return (
                    <th scope="col" key={d} aria-current={d === auj ? 'date' : undefined}
                      /* Dit à la voix de synthèse ce que la couleur dit à l'œil :
                         un repère qui n'existe qu'en couleur n'existe pas pour
                         tout le monde. Le MOT, lui, n'est pas écrit dans la
                         case — cf. la vue Semaine (RQ, 05/10/2026). */
                      title={d === auj ? 'Aujourd’hui' : undefined}
                      style={{ ...cellBase, background: weekend ? 'var(--gray-50)' : 'var(--gray-25)', fontSize: 9.5, lineHeight: 1.15, padding: '3px 0',
                        ...bandeAuj(d === auj), ...(d === auj ? TETE_AUJ : {}) }}>
                      <button type="button" className="pm-jour" onClick={() => setJourOuvert(d)}
                        aria-label={`Voir la journée du ${libelleJourLong(d)}`}
                        title={`Voir la journée du ${libelleJourLong(d)}`}
                        style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', font: 'inherit', color: 'inherit', width: '100%' }}>
                        <div style={{ color: d === auj ? 'var(--surface-card)' : 'var(--text-muted)' }}>{JOURS_COURTS[js]}</div>
                        <div style={{ fontVariantNumeric: 'tabular-nums', textDecoration: 'underline', textDecorationStyle: 'dotted', textUnderlineOffset: 2 }}>{Number(d.slice(8))}</div>
                      </button>
                      <LignesConventions liste={conventionsDuJour.get(d)} largeur="80%" pistes={pistesConv} />
                    </th>
                  );
                })}
                {avecTotal && COLS_TOTAL.map(([k, lib, aide]) => (
                  <th scope="col" key={k} title={aide} style={{ ...cellBase, background: 'var(--gray-25)', fontSize: 'var(--tableau-entete-compacte-police)', lineHeight: 1.1, padding: 'var(--tableau-ligne-compacte-padding-y) var(--tableau-ligne-compacte-padding-x)' }}>{lib}</th>
                ))}
              </tr>
            </thead>
            {groupes.map((g) => (
            <tbody key={g.cle}>
              {/* L'intitulé du bloc, sur toute la largeur. `th` et non `td` :
                  c'est un en-tête de section, et un lecteur d'écran doit
                  l'annoncer comme tel avant les lignes qu'il coiffe. */}
              {g.libelle ? (
                <tr>
                  <th colSpan={jours.length + (avecTotal ? 1 + COLS_TOTAL.length : 1)} scope="colgroup"
                    title={g.note || undefined}
                    style={{ ...cellBase, textAlign: 'start', padding: 'var(--tableau-ligne-compacte-padding-y) var(--tableau-ligne-compacte-padding-x)', background: 'var(--gray-50)',
                      fontSize: 'var(--tableau-entete-compacte-police)', fontWeight: 700, letterSpacing: '.04em', textTransform: 'uppercase',
                      color: 'var(--text-muted)', position: 'sticky', insetInlineStart: 0 }}>
                    {g.libelle}
                  </th>
                </tr>
              ) : null}
              {g.lignes.map((l) => (
                <tr key={l.cle}>
                  <th scope="row" style={{ ...cellBase, textAlign: 'start', padding: 'var(--tableau-ligne-compacte-padding-y) var(--tableau-ligne-compacte-padding-x)', background: 'var(--surface-raised)', position: 'sticky', insetInlineStart: 0, zIndex: 1, fontWeight: 600, fontSize: 'var(--tableau-entete-compacte-police)' }}>
                    <div className="pm-titre" style={vue === 'poste'
                      ? { whiteSpace: 'normal', lineHeight: 1.25, overflowWrap: 'anywhere' }
                      : { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {vue === 'personne' ? (
                        <button type="button" className="pm-jour" aria-pressed={suivi === l.cle}
                          title={suivi === l.cle ? 'Ne plus suivre cette personne' : 'Suivre cette personne : les autres s’estompent'}
                          onClick={() => basculerSuivi(l.cle)}
                          style={{ background: 'none', border: 'none', padding: 0, font: 'inherit', color: 'inherit', cursor: 'pointer', textAlign: 'start', display: 'block', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis',
                            ...(suivi === l.cle ? { textDecoration: 'underline', textUnderlineOffset: 2 } : {}) }}>{l.titre}</button>
                      ) : l.titre}
                    </div>
                    <div className="pm-sous" style={{ fontWeight: 400, fontSize: 9.5, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>{l.sous}</div>
                  </th>
                  {jours.map((d) => cellule(l, d))}
                  {avecTotal && (() => {
                    const t = totauxLigne(l.cle);
                    return COLS_TOTAL.map(([k]) => (
                      <td key={k} style={{ ...cellBase, fontVariantNumeric: 'tabular-nums', fontWeight: 700, background: 'var(--gray-25)' }}>
                        {k === 'gardes' ? (t.gardes || '') : t[k]}
                      </td>
                    ));
                  })()}
                </tr>
              ))}
            </tbody>
            ))}
          </table>
        </div>
      )}

      <div className="pm-legende" style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginTop: 10, fontSize: 12.5, color: 'var(--text-muted)', alignItems: 'center' }}>
        <span><span style={{ display: 'inline-block', width: 11, height: 11, background: 'var(--red-50)', border: '1px solid var(--red-500)', borderRadius: 'var(--radius-xs)', verticalAlign: '-1px' }} /> ouvert, personne</span>
        <span><span style={{ display: 'inline-block', width: 11, height: 11, background: 'var(--gray-100)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-xs)', verticalAlign: '-1px' }} /> fermé</span>
        <span><span style={{ display: 'inline-block', width: 11, height: 11, background: 'transparent', outline: '1.5px solid var(--amber-500)', borderRadius: 'var(--radius-xs)', verticalAlign: '-1px' }} /> dérogation</span>
        {/* Le liseré du jour même se nomme ICI et non dans la grille : sur le
            papier comme à l'écran, une bande colorée sans clé reste une énigme
            — mais la clé n'a pas à occuper la colonne la plus étroite. */}
        <span><span style={{ display: 'inline-block', width: 11, height: 11, background: 'var(--color-primary-active)', borderRadius: 'var(--radius-xs)', verticalAlign: '-1px' }} /> aujourd’hui</span>
        <span>Barré — planifié(e) mais absent(e) ; en vue par poste, la case passe en rouge si personne ne tient le poste</span>
        <span>La couleur d’une pastille est propre à {vue === 'poste' ? 'la personne' : 'au poste'} : elle ne change pas d’un mois à l’autre.</span>
      </div>
      {/* LA LÉGENDE DES CONVENTIONS (Inès, 05/10/2026) — SÉPARÉE de la légende
          ci-dessus, et ce n'est pas un oubli de rangement : celle du dessus
          explique les ÉTATS d'une case (ouvert sans personne, fermé,
          dérogation), celle-ci nomme des CLINIQUES. Les fondre aurait mis sur
          la même ligne un code d'alerte et un annuaire de partenaires. */}
      <LegendeConventions pistes={pistesConv} />
      {/* LA CLÉ DES INITIALES (30/09/2026) — sur le papier, pas de survol : une
          initiale ne se lit qu'avec le nom à côté. */}
      {vue === 'poste' && !enPrenoms && (() => {
        /* LES DIMANCHES / FÉRIÉS ET LES NUITS de chacun (RQ, 30/09/2026) : le
           nombre de postes tenus un dimanche ou un jour férié (0 s'affiche),
           et les nuits de garde planifiées (rien si aucune). Un poste dont la
           personne est absente n'est pas compté : elle ne le tient pas. */
        const chomes = new Set(joursChomesDuMois(rhMois));
        /* Comptés en VACATIONS, comme les totaux de la vue par personne. */
        const parJour = new Map<string, typeof rhAffectationsMois>();
        const vus = new Map<string, { nom: string; ouvres: number; dimFer: number; nuits: number }>();
        for (const x of rhAffectationsMois) {
          const o = x.personnel?.orgId;
          if (!o) continue;
          if (!vus.has(o)) vus.set(o, { nom: nomDe(x.personnel) || o, ouvres: 0, dimFer: 0, nuits: 0 });
          if (absentLe(o, x.date)) continue;
          const k = `${o}|${x.date}`;
          parJour.set(k, [...(parJour.get(k) || []), x]);
        }
        for (const [k, cases] of parJour) {
          const [o, d] = k.split('|');
          const v = vus.get(o)!;
          v.nuits += cases.filter((c) => c.poste?.code === 'GARDE').length;
          if (chomes.has(d) || new Date(`${d}T12:00:00`).getDay() === 0) v.dimFer += vacationsDuJour(cases);
          else v.ouvres += vacationsDuJour(cases);
        }
        const liste = [...vus.entries()].map(([o, v]) => ({ o, ...v, ini: initialesEffectives.get(o) || '?' }))
          .sort((x, y) => x.ini.localeCompare(y.ini));
        return liste.length ? (
          <div className="pm-legende" style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8, fontSize: 12, color: 'var(--text-body)', alignItems: 'center' }}>
            <strong style={{ fontSize: 12, marginInlineEnd: 4 }}>Personnes :</strong>
            {liste.map((x) => {
              const st = pastilleDe(x.o, couleurDe(x.o));
              return (
                <span key={x.o} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginInlineEnd: 6 }}>
                  <button type="button" className="pm-jour" aria-pressed={suivi === x.o}
                    title={suivi === x.o ? 'Ne plus suivre cette personne' : 'Suivre cette personne : les autres s’estompent dans la grille'}
                    onClick={() => basculerSuivi(x.o)}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: 'none', border: 'none', padding: 0, font: 'inherit', color: 'inherit', cursor: 'pointer' }}>
                    <span className="pm-pastille" style={{ ...st, border: `1px solid ${st.borderColor}`, borderRadius: 'var(--radius-xs)', fontSize: 10, fontWeight: 700, padding: '0 4px', lineHeight: '15px',
                      ...(suivi === x.o ? { outline: '2px solid var(--color-primary)', outlineOffset: 1 } : {}) }}>{x.ini}</span>
                    {x.nom}
                  </button>
                  <span title="Vacations tenues du lundi au samedi (hors jours fériés) ce mois-ci" style={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>· {x.ouvres} O</span>
                  {x.nuits > 0 && (
                    <span title="Nuits de garde planifiées ce mois-ci" style={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>· {x.nuits} G</span>
                  )}
                  <span title="Vacations tenues un dimanche ou un jour férié ce mois-ci" style={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>· {x.dimFer} D/F</span>
                </span>
              );
            })}
          </div>
        ) : null;
      })()}
      {/* LA LÉGENDE DES POSTES (29/09/2026) : une pastille de deux lettres ne se
          lit qu'avec sa clé à côté. */}
      {vue === 'personne' && postesDuMois.length > 0 && (
        <div className="pm-legende" style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8, fontSize: 12, color: 'var(--text-body)', alignItems: 'center' }}>
          <strong style={{ fontSize: 12, marginInlineEnd: 4 }}>Postes :</strong>
          {postesDuMois.map((p) => {
            const st = pastilleDe(p.code, couleurDe(p.code));
            return (
              <span key={p.code} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginInlineEnd: 6 }}>
                <span style={{ ...st, border: `1px solid ${st.borderColor}`, borderRadius: 'var(--radius-xs)', fontSize: 10, fontWeight: 700, padding: '0 4px', lineHeight: '15px' }}>
                  {abreges.get(p.code) || abregePoste(p.code)}
                </span>
                {p.libelle}
              </span>
            );
          })}
        </div>
      )}
      {/* ══ LA JOURNÉE DU LABORATOIRE (RQ, 05/10/2026) ════════════════════════
          Quels postes sont ouverts, qui les tient, et ce que PERSONNE ne tient.
          ⚠️ Aucune requête : tout vient de `rhAffectationsMois` et
          `rhOuverturesMois`, déjà chargés pour la grille. Les flèches restent
          donc dans le mois — en sortir n'aurait rien montré, et une fenêtre
          vide aurait affirmé « personne n'est planifié ». */}
      {jourOuvert && (() => {
        const affsJour = rhAffectationsMois.filter((a) => a.date === jourOuvert);
        const parPoste = new Map<string, typeof affsJour>();
        for (const a of affsJour) {
          const c = a.poste?.code;
          if (!c || !posteVisible(c)) continue;
          parPoste.set(c, [...(parPoste.get(c) || []), a]);
        }
        /* ⚠️ ON N'AFFICHE PAS LES 24 POSTES : un poste FERMÉ et sans personne
           n'est pas une information pour une journée — vingt lignes « fermé »
           noieraient le trou qu'on cherche. On garde ce qui est OUVERT, ce qui
           porte quelqu'un (même fermé : c'est alors une anomalie qui doit se
           voir), et on COMPTE ce qu'on écarte : un filtre muet ferait lire un
           écran incomplet comme un écran complet. */
        const retenus: { poste: RhPoste; o: RhOuverture | undefined; cases: typeof affsJour }[] = [];
        let fermesSansPersonne = 0;
        for (const p of postes) {
          if (!posteVisible(p.code)) continue;
          const o = ouvertures.get(`${p.code}|${jourOuvert}`);
          const cases = parPoste.get(p.code) || [];
          const ferme = o?.ouverture.etat === 'ferme';
          if (ferme && !cases.length) { fermesSansPersonne += 1; continue; }
          if (!o && !cases.length) { fermesSansPersonne += 1; continue; }
          retenus.push({ poste: p, o, cases });
        }
        const chome = chomesMois.has(jourOuvert);
        const dansLeMois = (iso: string) => iso.slice(0, 7) === rhMois;
        return (
          <FenetreJour date={jourOuvert} onClose={() => setJourOuvert(null)} largeur={720}
            eyebrow="Planning du laboratoire"
            conventions={conventionsDuJour.get(jourOuvert)}
            onDate={(iso) => { if (dansLeMois(iso)) setJourOuvert(iso); }}>
            {chome && (
              <p role="note" style={{ margin: '0 0 12px', padding: '8px 11px', borderRadius: 'var(--radius-md)', background: 'var(--amber-50)', color: 'var(--amber-600)', fontSize: 12.5, lineHeight: 1.45 }}>
                Jour chômé. ⚠️ Le laboratoire n’est pas fermé pour autant : les postes ouverts
                ci-dessous sont à tenir.
              </p>
            )}
            <RubriqueJour titre={`Postes — ${retenus.length} à l’affiche`}>
              {!retenus.length ? (
                <p style={{ margin: 0, fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.5 }}>
                  Aucun poste ouvert et aucune affectation ce jour-là
                  {fermesSansPersonne > 0 ? ` (${fermesSansPersonne} poste(s) fermé(s) ou sans ouverture déclarée).` : '.'}
                </p>
              ) : (
                <div style={{ display: 'grid', gap: 6 }}>
                  {retenus.map(({ poste: p, o, cases }) => {
                    const tousAbsents = cases.length > 0
                      && cases.every((c) => !!absentLe(c.personnel?.orgId || '', jourOuvert));
                    const trou = (o?.ouverture.etat === 'ouvert' && cases.length === 0) || tousAbsents;
                    const ferme = o?.ouverture.etat === 'ferme';
                    return (
                      <div key={p.code} style={{
                        display: 'grid', gap: 4, padding: '8px 11px', borderRadius: 'var(--radius-md)',
                        background: trou ? 'var(--red-50)' : ferme ? 'var(--gray-100)' : 'var(--surface-card)',
                        border: `1px solid ${trou ? 'var(--red-500)' : 'var(--border-default)'}`,
                      }}>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
                          <strong style={{ fontSize: 13 }}>{p.libelle}</strong>
                          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11.5, color: 'var(--text-muted)' }}>{p.code}</span>
                          {p.plageDebut && p.plageFin && (
                            <span style={{ fontSize: 11.5, color: 'var(--text-muted)', fontVariantNumeric: 'tabular-nums' }}>
                              {p.plageDebut}–{p.plageFin}
                            </span>
                          )}
                          <span style={{ marginInlineStart: 'auto', fontSize: 11.5, color: 'var(--text-muted)' }}>
                            {ferme ? `fermé${o?.ouverture.motif ? ` — ${o.ouverture.motif}` : ''}`
                              : o ? `${cases.length}/${o.ouverture.effectifAttendu} attendu(s)`
                                : 'ouverture non déclarée'}
                          </span>
                        </div>
                        {/* Qui tient le poste. Les ABSENTS restent affichés,
                            barrés : retirer la personne ferait perdre qui
                            devait tenir le poste ; la laisser telle quelle
                            ferait croire qu'elle l'a tenu. */}
                        {cases.length > 0 && (
                          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                            {cases.map((c) => {
                              const cle = c.personnel?.orgId || '?';
                              const pa = pastilleDe(cle, couleurDe(cle));
                              const absente = absentLe(cle, jourOuvert);
                              return (
                                <span key={c.documentId} title={absente ? `${nomDe(c.personnel)} — ${absente.motifLabel || 'absent(e)'}` : undefined}
                                  style={{
                                    display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12.5,
                                    borderRadius: 'var(--radius-pill)', padding: '2px 9px 2px 3px',
                                    background: pa.background, color: pa.color, border: `1px solid ${pa.borderColor}`,
                                    outline: c.derogation ? '1.5px solid var(--amber-500)' : undefined,
                                    opacity: absente ? 0.55 : 1,
                                    textDecoration: absente ? 'line-through' : undefined,
                                  }}>
                                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, fontWeight: 700, borderRadius: 'var(--radius-xs)', padding: '0 3px', background: 'rgba(255,255,255,0.55)' }}>
                                    {initialesEffectives.get(cle) || '?'}
                                  </span>
                                  {nomDe(c.personnel) || cle}
                                  {(c.heureDebut || c.heureFin) && (
                                    <span style={{ fontVariantNumeric: 'tabular-nums', opacity: 0.8 }}>
                                      {c.heureDebut}–{c.heureFin}
                                    </span>
                                  )}
                                </span>
                              );
                            })}
                          </div>
                        )}
                        {trou && (
                          <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--red-600)' }}>
                            {tousAbsents
                              ? 'Personne ne tient ce poste : tous les planifiés sont absents.'
                              : `Ouvert et non couvert${o?.manque ? ` — ${o.manque} manquant(e)` : ''}.`}
                          </span>
                        )}
                        {/* La dérogation ne se cache pas : c'est le seul cas où
                            quelqu'un tient un poste qu'il n'est pas habilité à
                            tenir. */}
                        {cases.filter((c) => c.derogation).map((c) => (
                          <span key={`d${c.documentId}`} style={{ fontSize: 12, color: 'var(--amber-600)', lineHeight: 1.45 }}>
                            {/* `derogation` est un OBJET (motif, auteur, date,
                                habilitations manquantes) et non une chaîne : on
                                lit `.motif`, et on nomme ce qui manquait —
                                c'est la raison d'être de la trace. */}
                            Dérogation — {nomDe(c.personnel) || '?'}
                            {c.derogation?.motif ? ` : ${c.derogation.motif}` : ''}
                            {c.derogation?.manquantes?.length
                              ? ` (manquait : ${c.derogation.manquantes.join(', ')})` : ''}
                          </span>
                        ))}
                      </div>
                    );
                  })}
                </div>
              )}
            </RubriqueJour>
            {/* ⚠️ CE QUI EST ÉCARTÉ SE DIT. */}
            {fermesSansPersonne > 0 && (
              <p style={{ margin: 0, fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.45 }}>
                {fermesSansPersonne} poste(s) non affiché(s) : fermé(s) ce jour-là ou sans calendrier
                d’ouverture, et sans personne affectée — il n’y a rien à y lire.
              </p>
            )}
          </FenetreJour>
        );
      })()}
      {aAjouter && (
        <ChoixRemplacant
          titre={`Ajouter sur « ${aAjouter.posteLibelle} »`}
          sousTitre={<>{libelleJourLong(aAjouter.date)} — seules les personnes qui tiennent le poste peuvent être choisies.</>}
          entete={seancesAjout.length > 1 ? (
            <div style={{ marginBottom: 10 }}>
              <Segment ariaLabel="Séance" valeur={seanceAjout} onChange={(v) => setSeanceAjout(String(v))}
                options={seancesAjout.map((x) => ({ valeur: x.cle, libelle: `${x.libelle} ${x.debut}–${x.fin}` }))} />
            </div>
          ) : undefined}
          candidats={candidatsAjout}
          onChoisir={ajouterSurCase}
          onClose={() => setAAjouter(null)}
        />
      )}
      {aRemplacer && (
        <ChoixRemplacant
          titre={`Remplacer ${aRemplacer.nom}`}
          sousTitre={<>« {aRemplacer.posteLibelle} » — {libelleJourLong(aRemplacer.date)}. La case garde ses heures ; seule la personne change.</>}
          candidats={candidatsRemplacement}
          onChoisir={(orgId) => remplacerPar(aRemplacer, orgId)}
          onClose={() => setARemplacer(null)}
        />
      )}
      </EchangeContext>
    </section>
  );
}
